/**
 * DocxMarkerWizard.tsx
 * Marker-based DOCX flow for Mini Printer.
 *
 * Flow: upload Excel + DOCX template → select sheet → detect template fields →
 * auto-map to Excel headers → review/patch mapping → choose grouping column →
 * review groups → bind & generate documents → preview → print / export PDF.
 *
 * Templates use docxtemplater placeholders:  Số PO: {{Số PO}}
 * Detail tables are wrapped in {{#items}} … {{/items}}.
 */

import * as React from "react";
import type { ParsedWorkbook } from "@/types/excel";
import type { Orientation, PaperSize, RenderedPage } from "@/types/print";

import { parseWorkbook } from "@/utils/parser";
import {
  parseMarkerDocxFile,
  fillDocxTemplateWithMarkers,
  renderDocxPreview,
} from "@/services/docxService";
import { renderDocxToPages, applyPageNumbering, isOverflowing, snapshotDocxSections } from "@/utils/docxRender";
import { mapPool } from "@/utils/mapPool";
import { PAGE_MARKER, TOTAL_MARKER, PAGE_VAR, NUMPAGES_VAR } from "@/utils/docxMarker";
import { ensureFooterZone, pageLabel } from "@/utils/fieldLayout";
import type { DocxMarkerField } from "@/utils/docxMarker";
import { looksLikeDateField, type FieldFormattingConfig } from "@/utils/formatter";
import {
  matchFieldToColumn,
  type FieldMappingResult,
} from "@/utils/docxFieldMatcher";
import {
  groupRowsDetailed,
  type DetailedGroup,
} from "@/utils/dataGrouper";
import { findValueSource, describeValueSource } from "@/utils/valueTracing";
import { classifyWorkbook } from "@/utils/workbookRoles";
import {
  analyzeDocxSections,
  duplicateSectionInDocx,
  deleteSectionInDocx,
  type SectionAnalysis,
} from "@/utils/docxSections";
import {
  hashBytes,
  sessionKey,
  saveSession,
  loadSession,
  clearSession,
  type SavedSession,
} from "@/utils/sessionStore";
import {
  joinSheets,
  suggestJoinKeys,
  summarizeJoinWarnings,
  type JoinWarning,
} from "@/utils/sheetJoin";
import {
  createEmptyRule,
  distinctValues,
  evaluatePages,
  describeRule,
  isAggregateOperator,
  isConditionComplete,
  NUMERIC_OPERATORS,
  OPERATOR_LABELS,
  ROW_OPERATORS,
  AGGREGATE_OPERATORS,
  selectApplicablePages,
  rowsByPage,
  VALUELESS_OPERATORS,
  validatePageConditions,
  type PageCondition,
  type PageConditionMap,
  type PageConditionOperator,
  type PageConditionOutcome,
} from "@/utils/pageCondition";
import {
  loadTemplateConfig,
  saveTemplateConfig,
  pageCountMismatchWarning,
  type TemplateConfig,
} from "@/utils/templateConfigStore";
import { generatePDF, printDocument, withPaper } from "@/utils/printExporter";
import {
  exportPdfsAsZip,
  downloadBlob,
  type GroupedPdfEntry,
} from "@/utils/exportZipper";
import {
  rewriteDocxPageSize,
  type PageSizeOverride,
} from "@/utils/docxPageSize";
import { downloadDocxFile } from "@/utils/defaultDocxTemplate";
import {
  validateMarkerExport,
  type ExportValidationResult,
} from "@/services/markerExportValidation";
import { resolveCartonCount } from "@/utils/cartonSplit";
import { buildFillFormats } from "@/utils/printEmptyPolicy";
import {
  buildGroupSearchText,
  matchesQuickSearch,
  matchesColumnFilters,
  activeColumnFilterCount,
  filterableColumns,
  suggestExtraColumns,
  type ColumnFilter,
} from "@/utils/printPageFilter";
import { PrintListFilterCard } from "@/components/DocxMarkerWizard/PrintListFilterCard";
import { DataStep } from "@/components/DocxMarkerWizard/DataStep";
import { PreviewContextBar } from "@/components/DocxMarkerWizard/PreviewContextBar";
import { SectionErrorBoundary } from "@/components/ErrorBoundary";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Download,
  Printer,
  AlertTriangle,
  CheckCircle2,
  FileSearch,
  Plus,
  Trash2,
  ChevronDown,
    ChevronUp,
    ListChecks,
  Pencil,
  ZoomIn,
  ZoomOut,
  Filter,
  EyeOff,
  X,
  ChevronRight,
  Archive,
    RefreshCw,
    FileText,
    FolderOpen,
  } from "lucide-react";
import { cn } from "@/lib/utils";
import { safeUUID } from "@/utils/safeUUID";
import { setupInteractiveDirectEdit } from "@/utils/interactiveDirectEdit";
import {
  captureTemplateLayoutEdits,
  applyLayoutEditsToContainer,
  applyLayoutEditsToPages,
  type TemplateLayoutEdit,
} from "@/utils/templateLayoutEdits";
import {
  applyTemplateOverlaysToContainer,
  applyTemplateOverlaysToPages,
  stripTemplateOverlaysFromPages,
  type TemplateOverlay,
} from "@/utils/templateOverlays";
import {
  buildPresetDocx,
  defaultFieldFormats,
  type PresetTemplateConfig,
} from "@/utils/presetTemplates";
import {
  applyPreviewTextEditsToDocx,
  snapshotParagraphInitialText,
} from "@/utils/docxTextPatch";
import {
  applyPreviewTableEditsToDocx,
  snapshotTableStructure,
} from "@/utils/docxTablePatch";
import { enableTemplateDrag, disableTemplateDrag } from "@/utils/templateDrag";
import { TemplateReviewStep } from "./TemplateReviewStep";
import { PresetTemplatePicker } from "./PresetTemplatePicker";
import { PrinterProfilesPanel } from "@/components/PrinterProfilesPanel";
import { formatPaper } from "@/utils/paperFormat";
import { type UserTemplate } from "@/utils/userTemplateStore";
import { toastSuccess, toastError, toastWarning, toastInfo } from "@/utils/toast";
import { recordRecentJob, updateRecentJobProgress, type JobKind } from "@/utils/recentJobsStore";
import { loadPrintPrefs, savePrintPrefs } from "@/utils/printPrefsStore";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import type { TourPhase } from "@/utils/onboardingStore";
import {
  saveTemplateEdits as persistTemplateEdits,
  loadTemplateEdits,
  clearTemplateEdits,
} from "@/utils/templateEditsStore";

type Step = "data" | "preview";

/**
 * Sentinel for the "no mapping" option: Radix Select rejects an empty string
 * as an item value.
 */
const NO_MAPPING = "__none__";

/** Sentinel for "follow the template" in the paper-size / orientation select. */
const AUTO_PAPER = "__auto__";

/** A user-added page in the Preview & Export step. */
interface AddedPage {
  id: string;
  groupValue: string;
  /** Display name; defaults to the group value but can be renamed. */
  label: string;
  group: DetailedGroup;
  /** Ticked state — only enabled pages are previewed / exported / printed. */
  enabled: boolean;
}

/**
 * One carton-split page: an identical copy of the split section (full table
 * kept) that carries only a different "k/R" carton number.
 */
interface SplitSubPage {
  id: string;
  /** Which phiếu (addedPage) this split came from. */
  originPageId: string;
  /** Carton ordinal — "1/4", "2/4"… */
  cartonIndex: number;
  cartonTotal: number;
  label: string;
  /** Pre-rendered single-section page with the carton badge already stamped. */
  rendered: RenderedPage;
}

/** A "Tách Kiện" action: the R sub-pages produced from one section of one phiếu. */
interface SplitCluster {
  id: string;
  originPageId: string;
  originLabel: string;
  /** Render index of the section that was split. */
  sectionIndex: number;
  /** Template pageId of the section that was split. */
  sectionPageId: number;
  /** Number of cartons produced (chosen field value, or section detail-row count). */
  originRowsCount: number;
  createdAt: number;
  enabled: boolean;
  subPages: SplitSubPage[];
}

/**
 * Inject a carton marker ("k/R") into the top-right corner of a rendered page,
 * without touching the template file. Falls back to returning the page unchanged
 * if no section element is found.
 */
function stampCartonLabel(rendered: RenderedPage, label: string): RenderedPage {
  const wrap = document.createElement("div");
  wrap.innerHTML = rendered.html;
  const section =
    (wrap.querySelector("section.docx") as HTMLElement | null) ??
    (wrap.firstElementChild as HTMLElement | null);
  if (!section) return rendered;
  section.style.position = "relative";

  // Word-style {{PAGE}} / {{NUMPAGES}} on this carton copy (label = "k/R").
  const [pageNum, totalNum] = label.split("/").map((s) => s.trim());
  if (pageNum) replaceTextAcrossNodes(section, PAGE_VAR, pageNum);
  if (totalNum) replaceTextAcrossNodes(section, NUMPAGES_VAR, totalNum);
  // Legacy ⟦PAGE⟧ / ⟦TOTAL⟧ aliases
  if (pageNum) replaceTextAcrossNodes(section, PAGE_MARKER, pageNum);
  if (totalNum) replaceTextAcrossNodes(section, TOTAL_MARKER, totalNum);

  // Remove any previous badges
  wrap.querySelectorAll(".carton-split-badge").forEach((b) => b.remove());

  // Hide top-right shape boxes/drawings from template so empty frames don't show
  const topDrawings = Array.from(
    section.querySelectorAll(".docx-drawing, .drawing, .shape, [data-docx-drawing], svg, foreignObject")
  ) as HTMLElement[];
  topDrawings.forEach((d) => {
    const topPx = parseFloat(d.style.top || "999");
    const rightPx = parseFloat(d.style.right || "999");
    const leftPx = parseFloat(d.style.left || "0");
    if (topPx < 50 && (rightPx < 80 || leftPx > 350)) {
      d.style.border = "none";
      d.style.boxShadow = "none";
      const svgs = Array.from(d.querySelectorAll("svg, rect, path, polygon")) as SVGElement[];
      svgs.forEach((s) => {
        s.setAttribute("stroke", "none");
        s.setAttribute("stroke-width", "0");
      });
      if (!d.textContent?.trim()) {
        d.style.display = "none";
      }
    }
  });

  const badge = document.createElement("div");
  badge.className = "carton-split-badge";
  badge.textContent = label;
  badge.style.cssText =
    "position:absolute;top:10px;right:16px;z-index:30;font-size:17px;font-weight:800;line-height:1;" +
    "color:#111111;background:transparent;padding:0;border:none;box-sizing:border-box;letter-spacing:0.5px;";
  section.appendChild(badge);
  return { ...rendered, html: wrap.innerHTML };
}

interface DocxFileState {
  fileName: string;
  fileData: ArrayBuffer | Uint8Array;
  fields: DocxMarkerField[];
  errors: string[];
  warnings: string[];
}

const STEPS: { key: Step; label: string }[] = [
  { key: "data", label: "1. Dữ Liệu & Nhóm" },
  { key: "preview", label: "2. Xem Trước & In" },
];

/**
 * Replace every occurrence of `search` with `replacement` across the text
 * nodes of `container`, even when Word split the marker into several nodes
 * (e.g. `{{` + `PAGE` + `}}` in different <w:t>/runs). Returns count.
 */
function replaceTextAcrossNodes(
  container: HTMLElement,
  search: string,
  replacement: string
): number {
  if (!search) return 0;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) nodes.push(n as Text);
  if (nodes.length === 0) return 0;

  // Build a flat string with node/offset mapping.
  const spans: { node: Text; start: number; end: number }[] = [];
  let full = "";
  for (const node of nodes) {
    const text = node.nodeValue ?? "";
    spans.push({ node, start: full.length, end: full.length + text.length });
    full += text;
  }

  let count = 0;
  let from = 0;
  while (true) {
    const idx = full.indexOf(search, from);
    if (idx === -1) break;
    count += 1;
    const end = idx + search.length;

    // Map offsets back to nodes.
    let startNode: Text | null = null;
    let startOff = 0;
    let endNode: Text | null = null;
    let endOff = 0;
    for (const s of spans) {
      if (idx >= s.start && idx < s.end) {
        startNode = s.node;
        startOff = idx - s.start;
      }
      if (end > s.start && end <= s.end) {
        endNode = s.node;
        endOff = end - s.start;
      }
      if (startNode && endNode) break;
    }
    if (!startNode || !endNode) break;

    // Replace using Range (handles cross-node deletion).
    const range = document.createRange();
    range.setStart(startNode, startOff);
    range.setEnd(endNode, endOff);
    range.deleteContents();
    range.insertNode(document.createTextNode(replacement));

    // Rebuild mapping for the next search (offsets shifted).
    const walker2 = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes2: Text[] = [];
    let n2: Node | null;
    while ((n2 = walker2.nextNode())) nodes2.push(n2 as Text);
    spans.length = 0;
    full = "";
    for (const node of nodes2) {
      const text = node.nodeValue ?? "";
      spans.push({ node, start: full.length, end: full.length + text.length });
      full += text;
    }
    from = idx + replacement.length;
  }
  return count;
}

/**
 * Number the rendered DOCX preview: replace footer page markers and always
 * inject a visible "Trang i/N" footer at the bottom of each page
 * (each <section class="docx"> = 1 page).
 *
 * `keptIndices` lists the sections that survived their display condition, so a
 * skipped page neither takes a number nor inflates the total.
 */
function numberPreviewPages(container: HTMLElement, keptIndices?: number[]): void {
  const sections = Array.from(container.querySelectorAll("section.docx"));
  const kept = keptIndices ?? sections.map((_, i) => i);
  const total = kept.length;
  sections.forEach((sec, sectionIndex) => {
    const el = sec as HTMLElement;
    const i = kept.indexOf(sectionIndex);
    if (i === -1) return;
    replaceTextAcrossNodes(el, PAGE_MARKER, String(i + 1));
    replaceTextAcrossNodes(el, TOTAL_MARKER, String(total));
    replaceTextAcrossNodes(el, PAGE_VAR, String(i + 1));
    replaceTextAcrossNodes(el, NUMPAGES_VAR, String(total));
    // Fixed footer band inside the paper safe zone (≈5–7 mm from bottom).
    ensureFooterZone(el, pageLabel(i + 1, total));
  });
}

export function DocxMarkerWizard({
  className,
  tourReplayNonce = 0,
  tourForcePhase,
}: {
  className?: string;
  /** Increment from PrintPage to force-start the quick tour. */
  tourReplayNonce?: number;
  tourForcePhase?: TourPhase | "all";
}) {
  // ── Upload / data ─────────────────────────────────────────
  const [workbook, setWorkbook] = React.useState<ParsedWorkbook | null>(null);
  /** Basename of the uploaded Excel file — shown in the preview summary chips. */
  const [excelFileName, setExcelFileName] = React.useState("");
  const [selectedSheet, setSelectedSheet] = React.useState("");
  const [docxFile, setDocxFile] = React.useState<DocxFileState | null>(null);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
const [dismissedAlerts, setDismissedAlerts] = React.useState<Set<string>>(new Set());

function dismissAlert(key: string) {
  setDismissedAlerts((prev) => new Set(prev).add(key));
}
function isAlertDismissed(key: string) {
  return dismissedAlerts.has(key);
}
  const [sectionInfo, setSectionInfo] = React.useState<SectionAnalysis | null>(null);
  /** Which Word section is bound; null means the whole template. */
  const [frameIndex, setFrameIndex] = React.useState<number | null>(null);
  const [excelHash, setExcelHash] = React.useState("");
  const [templateHash, setTemplateHash] = React.useState("");
  /** A session found for the current pair of files, awaiting the user's decision. */
  const [restorable, setRestorable] = React.useState<SavedSession | null>(null);

  // ── Mapping ───────────────────────────────────────────────
  const [mappings, setMappings] = React.useState<Record<string, FieldMappingResult>>({});
  const [formats, setFormats] = React.useState<Record<string, FieldFormattingConfig>>({});
  /** Field name → explicit required override. Unset falls back to the template default. */
  const [requiredOverrides, setRequiredOverrides] = React.useState<Record<string, boolean>>({});
  const [validation, setValidation] = React.useState<ExportValidationResult | null>(null);
  /** Post-render note (e.g. pages that overflowed) shown after an export. */
  const [exportNote, setExportNote] = React.useState<string | null>(null);
  /**
   * User-chosen paper size + orientation. Both must be set to take effect;
   * otherwise null (= follow the template, the default). When set, the active
   * DOCX is page-size-rewritten so preview/PDF/print all lay out at that size
   * (e.g. A5 landscape) instead of the template's own.
   */
  const [printPaperSize, setPrintPaperSize] = React.useState<"" | PaperSize>(() => loadPrintPrefs().paperSize ?? "");
  const [printPaperOrient, setPrintPaperOrient] = React.useState<"" | Orientation>(() => loadPrintPrefs().orientation ?? "");
  /** Custom dimensions (mm) when paperSize === "Custom". */
  const [customPaperMm, setCustomPaperMm] = React.useState<{ width: string; height: string }>(() => {
    const p = loadPrintPrefs().customMm;
    return p ? { width: String(p.width), height: String(p.height) } : { width: "100", height: "150" };
  });
  const customMm = React.useMemo(() => {
    const w = parseFloat(customPaperMm.width);
    const h = parseFloat(customPaperMm.height);
    if (!(w > 0) || !(h > 0)) return undefined;
    return { width: w, height: h };
  }, [customPaperMm]);
  const printPaper: PageSizeOverride = React.useMemo(() => {
    if (!printPaperSize || !printPaperOrient) return null;
    if (printPaperSize === "Custom" && !customMm) return null;
    return { paperSize: printPaperSize, orientation: printPaperOrient, customMm };
  }, [printPaperSize, printPaperOrient, customMm]);

  // Persist paper/printer prefs across sessions (Khổ Giấy & Máy In).
  React.useEffect(() => {
    savePrintPrefs({
      paperSize: printPaperSize || undefined,
      orientation: printPaperOrient || undefined,
      customMm: customMm,
    });
  }, [printPaperSize, printPaperOrient, customMm]);

  /** Provenance of the value the user last clicked on in the preview. */
  const [traceInfo, setTraceInfo] = React.useState<string | null>(null);
  const [headerFields, setHeaderFields] = React.useState<DocxMarkerField[]>([]);
  const [itemFields, setItemFields] = React.useState<DocxMarkerField[]>([]);

  // ── Secondary sheet lookup ────────────────────────────────
  const [lookupSheet, setLookupSheet] = React.useState("");
  const [joinKeyPrimary, setJoinKeyPrimary] = React.useState("");
  const [joinKeySecondary, setJoinKeySecondary] = React.useState("");

  /** Auto-detected roles for a macro-style workbook (VINMART / WIN…). */
  const [workbookRoleSummary, setWorkbookRoleSummary] = React.useState("");

  // ── Grouping (merged into the Mapping step) ────────────────
  const [groupingColumns, setGroupingColumns] = React.useState<string[]>([]);
  const [groups, setGroups] = React.useState<DetailedGroup[]>([]);
  const [groupSearch, setGroupSearch] = React.useState("");

  // Persist the chosen grouping column (Nhóm Dữ Liệu) across sessions.
  React.useEffect(() => {
    const col = groupingColumns[0];
    if (col) savePrintPrefs({ ...loadPrintPrefs(), groupingColumn: col });
  }, [groupingColumns]);
    /** When true, the print list shows only the ticked (enabled) items. */
    const [printFilterMode, setPrintFilterMode] = React.useState(false);
    /** Structured column filters (AND) on the preview print list. */
    const [columnFilters, setColumnFilters] = React.useState<ColumnFilter[]>([]);
    /** Extra columns shown as muted subtitles under each list label. */
    const [listExtraColumns, setListExtraColumns] = React.useState<string[]>([]);
    /** Expand advanced column-filter panel. */
        const [columnFilterOpen, setColumnFilterOpen] = React.useState(false);
        /**
         * Missing Excel / unmapped fields print blank instead of {{placeholder}}.
         * Default on — production printouts should not show template ghosts.
         */
        const [printEmptyWhenMissing, setPrintEmptyWhenMissing] = React.useState(true);
        /** Collapsed auto-map panel in the Preview step (mapping is optional). */
        const [mappingPanelOpen, setMappingPanelOpen] = React.useState(false);
const [previewPanelOpen, setPreviewPanelOpen] = React.useState(true);
const [exportCardOpen, setExportCardOpen] = React.useState(true);

  // ── Preview / export ──────────────────────────────────────
  const [step, setStep] = React.useState<Step>("data");
  const [busy, setBusy] = React.useState(false);

  // ── User-added pages (only these are previewed / printed / exported) ──
  const [addedPages, setAddedPages] = React.useState<AddedPage[]>([]);
  const [activePageId, setActivePageId] = React.useState<string>("");
  const [editingPageId, setEditingPageId] = React.useState<string>("");

  // ── Preview viewport (one physical page at a time + zoom) ──
  const [zoom, setZoom] = React.useState(1);
  const [sectionCount, setSectionCount] = React.useState(0);
  const [activeSection, setActiveSection] = React.useState(0);
  const [overflowSections, setOverflowSections] = React.useState<number[]>([]);

  // ── Carton splitting (Tách Kiện) ──────────────────────────
  const [splitClusters, setSplitClusters] = React.useState<SplitCluster[]>([]);
  /** When set, the preview shows this split sub-page instead of the normal page. */
  const [activeSplitPageId, setActiveSplitPageId] = React.useState<string>("");
  /**
   * Excel column whose value decides how many cartons to split into (e.g. pick
   * "Số kiện" = 4 → 4 cartons). Empty string = split by detail-row count.
   */
  const [splitField, setSplitField] = React.useState("");

  // Leaving one phiếu for another ends any carton-split view for the old page.
  React.useEffect(() => {
    setActiveSplitPageId("");
  }, [activePageId]);

  // ── Condition Page: per-page display conditions ───────────
  /** pageId (template section index) → rule. Persisted with the template. */
  const [pageConditions, setPageConditions] = React.useState<PageConditionMap>({});
  /** Page whose conditions are being edited in the panel. */
  const [conditionPageId, setConditionPageId] = React.useState(0);
  /** Inline editor above the preview; opens itself on a page that has a rule. */
  const [conditionPanelOpen, setConditionPanelOpen] = React.useState(false);
  /** Config as stored for the current template, used to spot a shape change. */
  const [loadedTemplateConfig, setLoadedTemplateConfig] =
    React.useState<TemplateConfig | null>(null);
  /** Template whose config has been loaded; guards the auto-save. */
  const loadedHashRef = React.useRef("");
  /** Sections of the preview kept for the record being viewed. */
  const [visibleSections, setVisibleSections] = React.useState<number[]>([]);
  const [skippedNotes, setSkippedNotes] = React.useState<string[]>([]);

  const previewRef = React.useRef<HTMLDivElement | null>(null);
    /** Filled+rendered page cache keyed by group + template/export fingerprint. */
    const renderCacheRef = React.useRef<Map<string, RenderedPage[]>>(new Map());
    const templatePreviewRef = React.useRef<HTMLDivElement | null>(null);
  const templateDragCleanupRef = React.useRef<(() => void) | null>(null);
  const [templateEditMode, setTemplateEditMode] = React.useState(false);
  /** The template canvas is opened on demand from the Preview & Export step. */
  const [templateEditorOpen, setTemplateEditorOpen] = React.useState(false);
  const [layoutEdits, setLayoutEdits] = React.useState<TemplateLayoutEdit[]>([]);
  /** Free-form text/image overlays added on top of template pages. */
  const [overlays, setOverlays] = React.useState<TemplateOverlay[]>([]);
  /** Preset template picker dialog open state. */
  const [presetPickerOpen, setPresetPickerOpen] = React.useState(false);
  /** Paper/printer profile editor dialog (opened from the preview step). */
  const [profileEditorOpen, setProfileEditorOpen] = React.useState(false);

  const primarySheet = React.useMemo(
    () =>
      workbook?.sheets.find((s) => s.name.toLowerCase() === selectedSheet.toLowerCase()) ?? null,
    [workbook, selectedSheet]
  );

  const secondarySheet = React.useMemo(
    () => workbook?.sheets.find((s) => s.name === lookupSheet) ?? null,
    [workbook, lookupSheet]
  );

  /** Primary rows plus any columns borrowed from the secondary sheet. */
  const joined = React.useMemo(() => {
    const baseRows = primarySheet?.rows ?? [];
    if (!secondarySheet || !joinKeyPrimary || !joinKeySecondary) {
      return { rows: baseRows, addedColumns: [] as string[], warnings: [] as JoinWarning[] };
    }
    return joinSheets(baseRows, secondarySheet, {
      primaryKey: joinKeyPrimary,
      secondaryKey: joinKeySecondary,
    });
  }, [primarySheet, secondarySheet, joinKeyPrimary, joinKeySecondary]);

  const rows = joined.rows;

  const headers = React.useMemo(
    () => [...(primarySheet?.headers ?? []), ...joined.addedColumns],
    [primarySheet, joined.addedColumns]
  );

  /** Columns whose values are (mostly) numbers — usable as a split count. */
  const numericColumns = React.useMemo(() => {
    return headers.filter((col) => {
      const samples = rows
        .slice(0, 20)
        .map((r) => r[col])
        .filter((v) => v != null && String(v).trim() !== "");
      if (samples.length === 0) return false;
      const numericCount = samples.filter((v) => Number.isFinite(Number(String(v).replace(/[^\d.]/g, "")))).length;
      return numericCount / samples.length >= 0.8;
    });
  }, [headers, rows]);

  /** Only primary columns can be used for grouping or as a join key. */
  const primaryHeaders = primarySheet?.headers ?? [];

  const joinWarningMessages = React.useMemo(
    () => summarizeJoinWarnings(joined.warnings),
    [joined.warnings]
  );

  const joinKeyCandidates = React.useMemo(
    () => (secondarySheet ? suggestJoinKeys(primaryHeaders, secondarySheet.headers) : []),
    [secondarySheet, primaryHeaders]
  );

  // ── Upload handlers ───────────────────────────────────────
  /** Drop everything that describes the data: a new workbook may be shaped differently. */
  function resetDataDerivedState() {
    setMappings({});
    setFormats({});
    setRequiredOverrides({});
    setGroupingColumns([]);
    setGroups([]);
    setAddedPages([]);
    setActivePageId("");
    setSplitClusters([]);
    setActiveSplitPageId("");
    setSplitField("");
    setLookupSheet("");
    setJoinKeyPrimary("");
    setJoinKeySecondary("");
    setWorkbookRoleSummary("");
    setValidation(null);
  }

  async function handleExcelUpload(file: File) {
    if (
      workbook &&
      !window.confirm(
        "Tải file Excel mới sẽ xoá mapping, cột gom nhóm và danh sách in hiện tại (template và chỉnh sửa layout được giữ). Tiếp tục?"
      )
    ) {
      return;
    }
    setBusy(true);
    setUploadError(null);
    try {
      const buffer = await file.arrayBuffer();
      const wb = await parseWorkbook(file, {});
      if (workbook) resetDataDerivedState();

      // Macro-style workbooks put print forms first — pick the data sheet, not a form.
      const roles = classifyWorkbook(wb.sheets);
      setWorkbook(wb);
      setSelectedSheet(roles.dataSheet || wb.sheets[0]?.name || "");
      if (roles.groupingColumns.length > 0) {
        // Only the primary grouping column is used (single-column grouping).
        setGroupingColumns(roles.groupingColumns.slice(0, 1));
      }
      // Re-apply the user's saved grouping column (Nhóm Dữ Liệu) when it still
      // exists in this workbook — so they don't have to re-pick it each time.
      const savedGroup = loadPrintPrefs().groupingColumn;
      const dataSheetName = roles.dataSheet || wb.sheets[0]?.name || "";
      if (savedGroup) {
        const sheet = wb.sheets.find((s) => s.name === dataSheetName);
        if (sheet && sheet.headers.includes(savedGroup)) {
          setGroupingColumns([savedGroup]);
        }
      }
      setWorkbookRoleSummary(roles.summary);
      setExcelHash(hashBytes(buffer));
      setExcelFileName(file.name);
      toastSuccess("Đã tải Excel", `${wb.sheets.length} sheet · ${wb.sheets[0]?.rows.length ?? 0} dòng`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Đọc file Excel thất bại.";
      setUploadError(msg);
      toastError("Tải Excel thất bại", msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleUsePresetTemplate(preset: PresetTemplateConfig) {
    setBusy(true);
    setUploadError(null);
    try {
      const data = buildPresetDocx(preset);
      const parsed = parseMarkerDocxFile(data, preset.fileName);
      if (docxFile) {
        setMappings({});
        setFormats({});
        setRequiredOverrides({});
        setLayoutEdits([]);
        setOverlays([]);
        // KHÔNG reset addedPages — danh sách phiếu (từ Excel) độc lập với template.
        // Chỉ đưa về trang đầu; templateHash đổi → effect preview tự re-bind lại.
        setActivePageId("");
        setValidation(null);
      }
      setDocxFile({
        fileName: parsed.fileName,
        fileData: parsed.fileData,
        fields: parsed.result.fields,
        errors: parsed.result.errors,
        warnings: parsed.result.warnings,
      });
      setHeaderFields(parsed.result.fields.filter((f) => !f.inRepeat));
      setItemFields(parsed.result.fields.filter((f) => f.inRepeat));
      setTemplateHash(hashBytes(data));
      clearTemplateEdits();
      setFrameIndex(null);
      setActiveSection(0);
      setVisibleSections([]);
      setSectionInfo(analyzeDocxSections(parsed.fileData, parsed.result.fields));
      // Seed default display formats for numeric detail columns (Số lượng →
      // number, Thành tiền/Đơn giá → ₫, % → percent) so presets render
      // formatted without extra setup.
      setFormats((prev) => ({ ...prev, ...defaultFieldFormats(preset.detailColumns) }));
      setExportNote(
        `Đã nạp mẫu "${preset.name}". Bạn có thể chỉnh sửa các trường, lặp bảng và tải file .docx về để dùng cho lần sau.`
      );
      toastSuccess("Đã nạp mẫu", preset.name);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Khởi tạo mẫu template thất bại.";
      setUploadError(msg);
      toastError("Mẫu template thất bại", msg);
    } finally {
      setBusy(false);
    }
  }

  function handleDownloadCurrentTemplate() {
    if (!docxFile) return;
    downloadDocxFile(docxFile.fileData, docxFile.fileName || "Template_Phieu_Giao_Hang_Chuan.docx");
  }

  /** Apply a saved user template's raw bytes as the active template. */
  async function handleUseUserTemplate(tpl: UserTemplate) {
    setBusy(true);
    setUploadError(null);
    try {
      const buffer = tpl.data;
      const parsed = parseMarkerDocxFile(buffer, tpl.name);
      if (docxFile) {
        setMappings({});
        setFormats({});
        setRequiredOverrides({});
        setLayoutEdits([]);
        setOverlays([]);
        // KHÔNG reset addedPages — danh sách phiếu (từ Excel) độc lập với template.
        // Chỉ đưa về trang đầu; templateHash đổi → effect preview tự re-bind lại.
        setActivePageId("");
        setValidation(null);
      }
      setDocxFile({
        fileName: parsed.fileName,
        fileData: parsed.fileData,
        fields: parsed.result.fields,
        errors: parsed.result.errors,
        warnings: parsed.result.warnings,
      });
      setHeaderFields(parsed.result.fields.filter((f) => !f.inRepeat));
      setItemFields(parsed.result.fields.filter((f) => f.inRepeat));
      setTemplateHash(hashBytes(buffer));
      clearTemplateEdits();
      setFrameIndex(null);
      setActiveSection(0);
      setVisibleSections([]);
      setSectionInfo(analyzeDocxSections(parsed.fileData, parsed.result.fields));
      // Restore saved editor state (layout edits, overlays, formats) so the
      // user template reproduces the exact edited document.
      if (tpl.layoutEdits?.length) setLayoutEdits(tpl.layoutEdits as TemplateLayoutEdit[]);
      if (tpl.overlays?.length) setOverlays(tpl.overlays as TemplateOverlay[]);
      if (tpl.formats && Object.keys(tpl.formats).length) {
        setFormats(tpl.formats as Record<string, FieldFormattingConfig>);
      }
      setExportNote(`Đã nạp mẫu "${tpl.name}".`);
      toastSuccess("Đã nạp mẫu", tpl.name);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Khởi tạo mẫu template thất bại.";
      setUploadError(msg);
      toastError("Mẫu template thất bại", msg);
    } finally {
      setBusy(false);
    }
  }

  // ── Auto-map when Excel headers / template change ─────────
  React.useEffect(() => {
    if (!docxFile || headers.length === 0) return;
    const fields = docxFile.fields;
    const fresh: Record<string, FieldMappingResult> = {};
    for (const f of fields) {
      const existing = mappings[f.fieldName];
      // Keep manual overrides; otherwise (re)auto-map.
      fresh[f.fieldName] =
        existing && existing.status === "mapped" && existing.excelColumn
          ? existing
          : matchFieldToColumn(f.fieldName, headers);
    }
    setMappings(fresh);
    // Seed DD/MM/YYYY for date-named fields when the user has not chosen a format yet.
    setFormats((prev) => {
      const next = { ...prev };
      for (const f of fields) {
        if (!looksLikeDateField(f.fieldName)) continue;
        if (next[f.fieldName]?.formatType && next[f.fieldName]?.formatType !== "text") continue;
        next[f.fieldName] = {
          ...next[f.fieldName],
          formatType: "date",
          dateFormat: next[f.fieldName]?.dateFormat || "DD/MM/YYYY",
        };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docxFile, headers]);

  // ── Apply an excelColumn onto the fields for fill ─────────
  const fieldsWithColumns = React.useMemo(() => {
    const all = [...headerFields, ...itemFields];
    return all.map((f) => ({
      ...f,
      excelColumn: mappings[f.fieldName]?.excelColumn ?? "",
    }));
  }, [headerFields, itemFields, mappings]);

  /**
   * Buffer used by EVERY render path (preview / PDF / ZIP / print / split).
   * When the user has chosen a paper size, this is the template with its
   * <w:pgSz> rewritten so docx-preview lays the content out at that size.
   * Falls back to the raw template when unset or when a rewrite fails.
   */
  const bindBuffer = React.useMemo(() => {
    if (!docxFile || !printPaper) return docxFile?.fileData ?? null;
    try {
      return rewriteDocxPageSize(docxFile.fileData, printPaper);
    } catch {
      return docxFile.fileData;
    }
  }, [docxFile, printPaper]);

  /** One entry per distinct field name (a label may appear on several pages). */
  const uniqueFields = React.useMemo(
    () => Array.from(new Map(fieldsWithColumns.map((f) => [f.fieldName, f])).values()),
    [fieldsWithColumns]
  );

  /**
     * Formats used by preview / PDF / print fill.
     * When printEmptyWhenMissing: always blank on missing data.
     * When off: preview shows {{tên}} for unmapped so user can spot gaps.
     */
    const fillFormats = React.useMemo(() => {
      const fieldFlags = uniqueFields.map((f) => {
        const m = mappings[f.fieldName];
        return {
          fieldName: f.fieldName,
          mapped: Boolean(m && m.status === "mapped" && m.excelColumn),
        };
      });
      return buildFillFormats(formats, fieldFlags, {
        printEmptyWhenMissing,
        showUnmappedPlaceholders: true,
      });
    }, [formats, uniqueFields, mappings, printEmptyWhenMissing]);

    /** Export path: never inject {{placeholder}}; still honor empty-missing policy. */
    const exportFormats = React.useMemo(() => {
      const fieldFlags = uniqueFields.map((f) => {
        const m = mappings[f.fieldName];
        return {
          fieldName: f.fieldName,
          mapped: Boolean(m && m.status === "mapped" && m.excelColumn),
        };
      });
      return buildFillFormats(formats, fieldFlags, {
        printEmptyWhenMissing,
        showUnmappedPlaceholders: false,
      });
    }, [formats, uniqueFields, mappings, printEmptyWhenMissing]);

    /**
     * A field is required unless the user says otherwise.
     */
  const isRequired = React.useCallback(
    (f: DocxMarkerField) => requiredOverrides[f.fieldName] ?? !f.inCondition,
    [requiredOverrides]
  );

  // ── Grouping ──────────────────────────────────────────────
  React.useEffect(() => {
    if (groupingColumns.length === 0) {
      setGroups([]);
      return;
    }
    setGroups(groupRowsDetailed(rows, groupingColumns));
  }, [groupingColumns, rows]);

  /**
   * Reconcile the print list with the computed groups: EVERY group becomes a
   * page in the single list. A page is "enabled" (ticked) when it should be
   * printed. Existing pages keep their order, label and tick when the same
   * groups reappear; brand-new groups come in ticked.
   */
  React.useEffect(() => {
    if (groups.length === 0) {
      setAddedPages([]);
      setActivePageId("");
      return;
    }
    setAddedPages((prev) => {
      const byValue = new Map(prev.map((p) => [p.groupValue, p]));
      // The print list shows ONLY the filter column (the last grouping column); the
      // primary grouping column just splits the data and is not shown.
      const filterCol = groupingColumns[groupingColumns.length - 1];
      const labelOf = (g: DetailedGroup): string => {
        if (!filterCol) return g.groupValue;
        return String(g.rows[0]?.[filterCol] ?? g.groupValue);
      };
      const next: AddedPage[] = [];
      for (const g of groups) {
        const existing = byValue.get(g.groupValue);
        if (existing) {
          next.push({ ...existing, group: g });
        } else {
          next.push({
            id: safeUUID(),
            groupValue: g.groupValue,
            label: labelOf(g),
            group: g,
            enabled: true,
          });
        }
      }
      return next;
    });
  }, [groups, groupingColumns]);

  // ── Print list management ─────────────────────────────────
  const activePage = addedPages.find((p) => p.id === activePageId) ?? addedPages[0] ?? null;

  /** Pages that are ticked — only these are previewed / exported / printed. */
  const enabledPages = addedPages.filter((p) => p.enabled !== false);

  function toggleEnabled(id: string, enabled: boolean) {
    setAddedPages((prev) => prev.map((p) => (p.id === id ? { ...p, enabled } : p)));
    if (enabled) setActivePageId(id);
  }

  function setAllEnabled(enabled: boolean) {
    setAddedPages((prev) => prev.map((p) => ({ ...p, enabled })));
  }

  /** Rows shown in the list: everything, or (in filter mode) only ticked ones. */
    const visiblePrintPages = React.useMemo(() => {
      const base = printFilterMode ? enabledPages : addedPages;
      const needle = groupSearch.trim();
      const hasColFilters = activeColumnFilterCount(columnFilters) > 0;
      if (!needle && !hasColFilters) return base;

      return base.filter((p) => {
        if (hasColFilters && !matchesColumnFilters(p.group, columnFilters)) return false;
        if (!needle) return true;
        const blob = buildGroupSearchText(p.group, {
          label: p.label,
          groupValue: p.groupValue,
        });
        return matchesQuickSearch(blob, needle);
      });
    }, [addedPages, enabledPages, printFilterMode, groupSearch, columnFilters]);

    const printFilterColumns = React.useMemo(
          () => filterableColumns(primaryHeaders),
          [primaryHeaders]
        );

        // Default subtitle columns under list labels.
        React.useEffect(() => {
          if (listExtraColumns.length > 0 || printFilterColumns.length === 0) return;
          const scored = suggestExtraColumns(printFilterColumns, groupingColumns, 2);
          if (scored.length > 0) setListExtraColumns(scored);
        }, [printFilterColumns, groupingColumns, listExtraColumns.length]);

  /** Move a page within the export list; this is also the PDF merge order. */
  function handleMovePage(id: string, direction: -1 | 1) {
    setAddedPages((prev) => {
      const index = prev.findIndex((p) => p.id === id);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function handleRenamePage(id: string, label: string) {
    setAddedPages((prev) =>
      prev.map((p) => (p.id === id ? { ...p, label: label.trim() || p.groupValue } : p))
    );
  }

  // ── Template preview & edit (Upload step) ────────────────
  async function renderTemplatePreview() {
    if (!docxFile || !templatePreviewRef.current) return;
    // Detach any existing interact.js drag handlers before replacing the DOM.
    templateDragCleanupRef.current?.();
    templateDragCleanupRef.current = null;
    setBusy(true);
    try {
      templatePreviewRef.current.innerHTML = "";
      await renderDocxPreview(docxFile.fileData, templatePreviewRef.current);
      // Re-apply any previously saved layout edits so they stay visible.
      if (layoutEdits.length > 0) {
        applyLayoutEditsToContainer(templatePreviewRef.current, layoutEdits);
      }
      // The canvas now mirrors the DOCX: record table positions so later
      // row/column edits can be diffed against it.
      snapshotTableStructure(templatePreviewRef.current);
      if (templateEditMode) {
        applyTemplateEditMode(true);
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Xem trước template thất bại.");
    } finally {
      setBusy(false);
    }
  }

  /** Normalize text editing + drag setup for the current template preview. */
  function applyTemplateEditMode(enabled: boolean) {
    const container = templatePreviewRef.current;
    if (!container) return;
    // Tear down previous interact.js drag (if any) before re-applying.
    if (templateDragCleanupRef.current) {
      templateDragCleanupRef.current();
      templateDragCleanupRef.current = null;
    }
    if (enabled) {
      // Snapshot paragraph text before edits so we can patch matching <w:p> later.
      snapshotParagraphInitialText(container);
      // Text editing (contentEditable) via the shared util, WITHOUT its native drag
      // (interact.js handles drag instead, avoiding double-handler conflicts).
      setupInteractiveDirectEdit(container, true, undefined, {
        drag: false,
        onChanged: () => setUploadError(null),
      });
      templateDragCleanupRef.current = enableTemplateDrag(container, () =>
        setUploadError(null)
      );
    } else {
      setupInteractiveDirectEdit(container, false);
      disableTemplateDrag(container);
    }
  }

  /**
   * Restore a saved canvas snapshot (undo/redo). Replaces the DOM and re-applies
   * edit mode so contenteditable + interact.js handlers are attached again.
   */
  function restoreTemplateCanvasSnapshot(html: string) {
    const container = templatePreviewRef.current;
    if (!container) return;
    // Tear down interact.js + contenteditable handlers on the old DOM.
    if (templateDragCleanupRef.current) {
      templateDragCleanupRef.current();
      templateDragCleanupRef.current = null;
    }
    setupInteractiveDirectEdit(container, false);
    container.innerHTML = html;
    snapshotTableStructure(container);
    snapshotParagraphInitialText(container);
    if (templateEditMode) {
      setupInteractiveDirectEdit(container, true, undefined, {
        drag: false,
        onChanged: () => setUploadError(null),
      });
      templateDragCleanupRef.current = enableTemplateDrag(container, () =>
        setUploadError(null)
      );
    }
  }

  /** Open the template canvas over the preview; the render effect fills it. */
  /**
   * Open the template editor. When `pageIdx` is given, the editor opens on that
   * Word section so the user edits the exact frame they clicked "Edit" on.
   */
  function openTemplateEditor(pageIdx?: number) {
    setUploadError(null);
    if (typeof pageIdx === "number" && pageIdx >= 0) setConditionPageId(pageIdx);
    setTemplateEditorOpen(true);
  }

  function closeTemplateEditor() {
    setTemplateEditMode(false);
    setTemplateEditorOpen(false);
  }

  /**
     * Duplicate the given Word section (page). The copy is inserted right after
     * `sectionIndex`. Returns the copy's section index on success, or false.
     *
     * Updates tab/active state immediately so Preview does not wait on the async
     * re-bind (stale `visibleSections` previously made "Nhân đôi" look broken).
     */
    async function duplicateSectionAt(sectionIndex: number): Promise<number | false> {
      if (!docxFile) return false;
      setBusy(true);
      try {
        const dup = duplicateSectionInDocx(docxFile.fileData, sectionIndex);
        const parsed = parseMarkerDocxFile(dup, docxFile.fileName);
        const info = analyzeDocxSections(parsed.fileData, parsed.result.fields);
        const newCount = info.sections.length;
        const copyIndex = Math.min(sectionIndex + 1, Math.max(0, newCount - 1));

        setDocxFile({
          fileName: parsed.fileName,
          fileData: parsed.fileData,
          fields: parsed.result.fields,
          errors: parsed.result.errors,
          warnings: parsed.result.warnings,
        });
        setHeaderFields(parsed.result.fields.filter((f) => !f.inRepeat));
        setItemFields(parsed.result.fields.filter((f) => f.inRepeat));
        setSectionInfo(info);
        // Working buffer is now the multi-page result — keep original in sync and
        // clear single-frame lock so pageIdOf stays 1:1 with section indices.
        setTemplateHash(hashBytes(parsed.fileData));
        setFrameIndex(null);

        // Optimistic tabs + jump to the copy (before renderPreview finishes).
        setSectionCount(newCount);
        setVisibleSections((prev) => {
          const all = info.sections.map((s) => s.index);
          if (prev.length === 0) return all;
          const next = prev.map((i) => (i > sectionIndex ? i + 1 : i));
          if (!next.includes(copyIndex)) {
            const at = next.findIndex((i) => i > copyIndex);
            if (at === -1) next.push(copyIndex);
            else next.splice(at, 0, copyIndex);
          }
          return next;
        });
        setActiveSection(copyIndex);
        setConditionPageId(copyIndex);
        setActiveSplitPageId("");

        setUploadError(null);
        setExportNote(
          `Đã nhân đôi Trang ${sectionIndex + 1} → đang xem bản sao (Trang ${copyIndex + 1}).`
        );
        toastSuccess("Đã nhân đôi trang", `Bản sao là Trang ${copyIndex + 1}`);
        return copyIndex;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Nhân đôi trang thất bại.";
        setUploadError(msg);
        toastError("Nhân đôi trang thất bại", msg);
        return false;
      } finally {
        setBusy(false);
      }
    }

    /** Delete the given Word section (page). Returns next active index, or false. */
    async function deleteSectionAt(sectionIndex: number): Promise<number | false> {
      if (!docxFile || !sectionInfo || sectionInfo.sections.length <= 1) {
        toastWarning("Không thể xóa trang duy nhất còn lại của template.");
        return false;
      }
      if (!window.confirm(`Xóa Trang ${sectionIndex + 1} khỏi template?`)) return false;
      setBusy(true);
      try {
        const del = deleteSectionInDocx(docxFile.fileData, sectionIndex);
        const parsed = parseMarkerDocxFile(del, docxFile.fileName);
        const info = analyzeDocxSections(parsed.fileData, parsed.result.fields);
        const newCount = info.sections.length;
        const nextActive = Math.min(sectionIndex, Math.max(0, newCount - 1));

        setDocxFile({
          fileName: parsed.fileName,
          fileData: parsed.fileData,
          fields: parsed.result.fields,
          errors: parsed.result.errors,
          warnings: parsed.result.warnings,
        });
        setHeaderFields(parsed.result.fields.filter((f) => !f.inRepeat));
        setItemFields(parsed.result.fields.filter((f) => f.inRepeat));
        setSectionInfo(info);
        setTemplateHash(hashBytes(parsed.fileData));
        setFrameIndex(null);

        setSectionCount(newCount);
        setVisibleSections((prev) => {
          const all = info.sections.map((s) => s.index);
          if (prev.length === 0) return all;
          return prev
            .filter((i) => i !== sectionIndex)
            .map((i) => (i > sectionIndex ? i - 1 : i));
        });
        setActiveSection(nextActive);
        setConditionPageId(nextActive);
        setActiveSplitPageId("");

        setUploadError(null);
        setExportNote(`Đã xóa Trang ${sectionIndex + 1}.`);
        toastSuccess("Đã xóa trang", `Trang ${sectionIndex + 1}`);
        return nextActive;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Xóa trang thất bại.";
        setUploadError(msg);
        toastError("Xóa trang thất bại", msg);
        return false;
      } finally {
        setBusy(false);
      }
    }

  /** Save into the DOCX, close the editor and let the preview re-bind data. */
  async function saveAndCloseTemplateEditor() {
    const saved = await saveTemplateEdits();
    if (saved) closeTemplateEditor();
  }

  async function toggleTemplateEdit() {
    if (!templatePreviewRef.current) return;
    const next = !templateEditMode;
    // Turning OFF edit mode: auto-save pending canvas text edits so the
    // preview / print sync immediately (not only after a manual "Lưu").
    if (!next && templatePreviewRef.current) {
      const saved = await saveTemplateEdits();
      if (!saved) return; // keep editing so the user can fix the error
    }
    setTemplateEditMode(next);
    applyTemplateEditMode(next);
  }

  /**
   * Persist canvas edits: write text/placeholder changes into the DOCX, re-parse
   * fields, and keep only non-text layout overlays (drag / format / hide).
   */
  async function saveTemplateEdits(): Promise<boolean> {
    if (!templatePreviewRef.current || !docxFile) return false;
    setBusy(true);
    try {
      const container = templatePreviewRef.current;
      // Text first: it lands in the original cells, which the structural pass
      // then carries around when it rebuilds rows/columns.
      const {
        fileData: textPatched,
        patchedCount: textCount,
        unmatched,
      } = applyPreviewTextEditsToDocx(docxFile.fileData, container);
      const {
        fileData: patched,
        patchedCount: tableCount,
        unsupported,
      } = applyPreviewTableEditsToDocx(textPatched, container);
      const patchedCount = textCount + tableCount;

      // Text that landed in the DOCX is dropped from overlays. If some patches
      // could not be written, keep their text overlays (incl. empty = cleared)
      // so Preview does not resurrect deleted lines like "Mã CH".
      const edits: TemplateLayoutEdit[] = captureTemplateLayoutEdits(container)
        .map((e) => {
          if (unmatched.length === 0 || e.text === undefined) {
            const { text: _omit, ...rest } = e;
            return rest as TemplateLayoutEdit;
          }
          return e;
        })
        .filter((e) => e.text !== undefined || Boolean(e.transform || e.format || e.hidden));

      if (patchedCount === 0 && unmatched.length === 0 && edits.length === 0) {
        setUploadError(null);
        setExportNote("Mẫu template được giữ nguyên bản gốc (chưa có thay đổi mới).");
        setTemplateEditMode(false);
        return true;
      }

      setLayoutEdits(edits);

      const problems: string[] = [];
      if (unmatched.length > 0) {
        const samples = unmatched
          .slice(0, 3)
          .map((p) => {
            const s = (p.to || p.from || "").replace(/\s+/g, " ").trim();
            return s.length > 40 ? `${s.slice(0, 40)}…` : s || "(trống)";
          })
          .join("; ");
        problems.push(
          `${unmatched.length} chỗ văn bản chưa ghi được vào file Word` +
            (samples ? ` (${samples})` : "")
        );
      }
      if (unsupported.length > 0) {
        problems.push(
          `${unsupported.length} bảng có ô gộp (merge) nên chỉ đổi được số hàng, chưa đổi được số cột`
        );
      }
      setUploadError(
        problems.length > 0
          ? `Chưa thể chốt template: đã ghi ${patchedCount} chỉnh sửa, nhưng ${problems.join("; ")}. Hãy xử lý lỗi này rồi lưu lại; hệ thống sẽ không chuyển sang Xem trước bằng bản cũ.`
          : null
      );

      if (patchedCount > 0) {
        const parsed = parseMarkerDocxFile(patched, docxFile.fileName);
        setDocxFile({
          fileName: parsed.fileName,
          fileData: parsed.fileData,
          fields: parsed.result.fields,
          errors: parsed.result.errors,
          warnings: parsed.result.warnings,
        });
        setHeaderFields(parsed.result.fields.filter((f) => !f.inRepeat));
        setItemFields(parsed.result.fields.filter((f) => f.inRepeat));
        setSectionInfo(analyzeDocxSections(parsed.fileData, parsed.result.fields));
        // Keep the unsliced original in sync when editing the whole template.
        if (frameIndex === null) {
          setTemplateHash(hashBytes(parsed.fileData));
        }
        // docxFile change triggers renderTemplatePreview via effect.
      }
      return problems.length === 0;
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Lưu chỉnh sửa template thất bại.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Clean up drag handlers when the wizard unmounts or the template changes.
  React.useEffect(() => {
    return () => {
      templateDragCleanupRef.current?.();
      templateDragCleanupRef.current = null;
    };
  }, []);

  // Fill the template canvas whenever the editor is open, so edit mode has
  // elements to act on without a manual "Tải Lại".
  React.useEffect(() => {
    if (docxFile && templateEditorOpen) {
      renderTemplatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docxFile, templateEditorOpen]);

  // ── Condition Page: model, persistence and evaluation ─────
  /**
   * Physical pages of the template. The Word sections are the intent, but the
   * renderer is the authority: if it emits more pages than there are sections,
   * every one of them still needs a configurable pageId.
   */
  const templatePageCount = Math.max(sectionInfo?.sections.length ?? 0, sectionCount, 1);

  /**
   * Render index → pageId. Binding a single frame renders just that section, so
   * its rendered page still belongs to the frame's pageId in the full template.
   */
  const pageIdOf = React.useCallback(
    (renderIndex: number) => (frameIndex === null ? renderIndex : frameIndex),
    [frameIndex]
  );

  const outcomesFor = React.useCallback(
    (recordRows: Record<string, unknown>[]): PageConditionOutcome[] =>
      evaluatePages(pageConditions, recordRows, templatePageCount),
    [pageConditions, templatePageCount]
  );

  /** Outcome per page for the record currently previewed. */
  const activeOutcomes = React.useMemo(
    () => (activePage ? outcomesFor(activePage.group.rows) : []),
    [activePage, outcomesFor]
  );

  const conditionValidation = React.useMemo(
    () => validatePageConditions(pageConditions, headers, templatePageCount),
    [pageConditions, headers, templatePageCount]
  );

  // Rules belong to the template: reload them whenever another template is in.
  // Keyed on the template alone — a changing page count must never reset the
  // rules the user is editing.
  React.useEffect(() => {
    if (!templateHash) return;
    const config = loadTemplateConfig(templateHash);
    setLoadedTemplateConfig(config);
    setPageConditions(config?.pageConditions ?? {});
    setConditionPageId(0);
    loadedHashRef.current = templateHash;
  }, [templateHash]);

  const templateShapeWarning = React.useMemo(
    () => pageCountMismatchWarning(loadedTemplateConfig, templatePageCount),
    [loadedTemplateConfig, templatePageCount]
  );

  // Saving before the load has run would overwrite the stored rules with the
  // empty state this component starts in.
  React.useEffect(() => {
    if (!templateHash || loadedHashRef.current !== templateHash) return;
    saveTemplateConfig(templateHash, {
      pageCount: templatePageCount,
      sheetName: selectedSheet,
      pageConditions,
    });
  }, [templateHash, templatePageCount, selectedSheet, pageConditions]);

  // Follow the page being previewed, so a rule is never written against the
  // page the user happens to have left selected in the dropdown.
  React.useEffect(() => {
    setConditionPageId(pageIdOf(activeSection));
  }, [activeSection, pageIdOf]);

  const conditionRule = pageConditions[String(conditionPageId)];

  // Landing on a page that already carries a rule should show that rule; an
  // unconfigured page stays out of the way behind its summary line.
  React.useEffect(() => {
    setConditionPanelOpen(
      (pageConditions[String(conditionPageId)]?.conditions.length ?? 0) > 0
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionPageId]);

  /** Conditions the user has not finished: they filter nothing and must say so. */
  const incompleteConditions = (conditionRule?.conditions ?? []).filter(
    (c) => !isConditionComplete(c)
  );

  function updateRule(pageId: number, update: (rule: ReturnType<typeof createEmptyRule>) => ReturnType<typeof createEmptyRule>) {
    setPageConditions((prev) => {
      const current = prev[String(pageId)] ?? createEmptyRule();
      return { ...prev, [String(pageId)]: update(current) };
    });
  }

  function addCondition(pageId: number) {
    updateRule(pageId, (rule) => ({
      ...rule,
      enabled: true,
      conditions: [
        ...rule.conditions,
        { id: safeUUID(), field: "", operator: "in" as PageConditionOperator, values: [] },
      ],
    }));
  }

  function patchCondition(pageId: number, conditionId: string, patch: Partial<PageCondition>) {
    updateRule(pageId, (rule) => ({
      ...rule,
      conditions: rule.conditions.map((c) => (c.id === conditionId ? { ...c, ...patch } : c)),
    }));
  }

  function removeCondition(pageId: number, conditionId: string) {
    updateRule(pageId, (rule) => ({
      ...rule,
      conditions: rule.conditions.filter((c) => c.id !== conditionId),
    }));
  }

  function clearPageConditions(pageId: number) {
    setPageConditions((prev) => {
      const next = { ...prev };
      delete next[String(pageId)];
      return next;
    });
  }

  /** Detail rows each page lists for a record, after its own row filter. */
  const rowsPerPage = React.useCallback(
    (recordRows: Record<string, unknown>[]) =>
      rowsByPage(pageConditions, recordRows, templatePageCount),
    [pageConditions, templatePageCount]
  );

  /** Drop the pages this record does not qualify for, before any numbering. */
  const keepApplicablePages = React.useCallback(
    (rendered: RenderedPage[], recordRows: Record<string, unknown>[]): RenderedPage[] =>
      selectApplicablePages(rendered, outcomesFor(recordRows), pageIdOf),
    [outcomesFor, pageIdOf]
  );

  // ── Carton splitting (Tách Kiện) ───────────────────────────
  /**
   * Split sub-pages from clusters that are ticked to be exported.
   */
  const enabledSplitSubPages = React.useMemo(
    () => splitClusters.filter((c) => c.enabled !== false).flatMap((c) => c.subPages),
    [splitClusters]
  );

  const activeSplitSubPage = React.useMemo(() => {
    if (!activeSplitPageId) return null;
    for (const cluster of splitClusters) {
      const sp = cluster.subPages.find((s) => s.id === activeSplitPageId);
      if (sp) return sp;
    }
    return null;
  }, [splitClusters, activeSplitPageId]);

  /**
   * Render + split ONE phiếu into identical carton copies and return a
   * `SplitCluster` (or null when its carton count is ≤ 1). The number of copies
   * is the chosen field's value on that phiếu (e.g. "Số kiện"=4 → 4 cartons)
   * or, when no field is chosen, the number of detail rows in the section. Each
   * sub-page keeps the whole table unchanged and only gains a "k/R" marker.
   */
  async function buildSplitClusterForPage(
    addedPage: AddedPage,
    sectionIndex: number
  ): Promise<SplitCluster | null> {
    if (!docxFile) return null;
    const pageId = pageIdOf(sectionIndex);
    const rows = rowsPerPage(addedPage.group.rows)[pageId] ?? [];
    const cartonCount = resolveCartonCount(addedPage.group, splitField, rows.length);
    if (cartonCount <= 1) return null; // nothing to split

    // Render the section ONCE with ALL its rows — every carton copy is an
    // identical page that differs only in the carton number stamped on it.
    const itemRowsBySection: Record<number, Record<string, unknown>[]> = {};
    for (let i = 0; i < templatePageCount; i += 1) itemRowsBySection[i] = [];
    itemRowsBySection[sectionIndex] = rows;
    const filled = await fillDocxTemplateWithMarkers(
      bindBuffer ?? docxFile.fileData,
      { rows: addedPage.group.rows, headerValues: addedPage.group.headerValues },
      {
        headerFields: fieldsWithColumns.filter((f) => !f.inRepeat),
        itemFields: fieldsWithColumns.filter((f) => f.inRepeat),
        formatByField: exportFormats,
        itemRowsBySection,
      }
    );
    const all = await renderDocxToPages(filled, { detectPaper: true });
    const targetIndex = all.findIndex((_, idx) => pageIdOf(idx) === pageId);
    const base = all[targetIndex] ?? all[pageId] ?? all[0];
    const [page] = applyTemplateOverlaysToPages(
      applyLayoutEditsToPages([base], layoutEdits),
      overlays
    );

    const subPages: SplitSubPage[] = [];
    for (let k = 0; k < cartonCount; k++) {
      const label = `${k + 1}/${cartonCount}`;
      subPages.push({
        id: safeUUID(),
        originPageId: addedPage.id,
        cartonIndex: k + 1,
        cartonTotal: cartonCount,
        label,
        rendered: stampCartonLabel(page, label),
      });
    }
    return {
      id: safeUUID(),
      originPageId: addedPage.id,
      originLabel: addedPage.label,
      sectionIndex,
      sectionPageId: pageId,
      originRowsCount: cartonCount,
      createdAt: Date.now(),
      enabled: true,
      subPages,
    };
  }

  /**
   * Auto-split: re-run the split for every ticked phiếu whenever the split
   * column (configured in Chỉnh Sửa Template) changes — no buttons needed
   * in Preview. Existing clusters are replaced, not appended.
   */
  const lastAutoSplitFieldRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!docxFile || addedPages.length === 0) return;
    const key = splitField ?? "";
    if (lastAutoSplitFieldRef.current === key) return;
    lastAutoSplitFieldRef.current = key;

    let cancelled = false;
    (async () => {
      const clusters: SplitCluster[] = [];
      for (const p of enabledPages) {
        if (cancelled) return;
        const cluster = await buildSplitClusterForPage(p, activeSection);
        if (cluster) clusters.push(cluster);
      }
      if (!cancelled) {
        setSplitClusters(clusters);
        setActiveSplitPageId("");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitField, docxFile, addedPages, enabledPages, activeSection]);

  // ── Preview render (driven by the active added page) ──────
  const renderPreview = React.useCallback(async () => {
    if (!docxFile) return;
    const page = addedPages.find((p) => p.id === activePageId) ?? addedPages[0];
    if (!page) {
      if (previewRef.current) previewRef.current.innerHTML = "";
      return;
    }
    setBusy(true);
    try {
      const filled = fillDocxTemplateWithMarkers(
        bindBuffer ?? docxFile.fileData,
        { rows: page.group.rows, headerValues: page.group.headerValues },
        {
          headerFields: fieldsWithColumns.filter((f) => !f.inRepeat),
          itemFields: fieldsWithColumns.filter((f) => f.inRepeat),
          /** Preview: unmapped show {{tên}} only when empty-missing setting is off. */
                    formatByField: fillFormats,
          itemRowsBySection: rowsPerPage(page.group.rows),
        }
      );
      if (previewRef.current) {
        await renderDocxPreview(filled, previewRef.current);
        if (layoutEdits.length > 0) {
          applyLayoutEditsToContainer(previewRef.current, layoutEdits);
        }
        if (overlays.length > 0) {
          applyTemplateOverlaysToContainer(previewRef.current, overlays);
        }

        const sections = Array.from(
          previewRef.current.querySelectorAll("section.docx")
        ) as HTMLElement[];

        // Pages this record does not qualify for leave the flow here: they are
        // neither numbered, counted nor shown.
        const outcomes = outcomesFor(page.group.rows);
        const kept = sections
          .map((_, i) => i)
          .filter((i) => outcomes[pageIdOf(i)]?.applicable !== false);
        numberPreviewPages(previewRef.current, kept);

                // Seed export cache from the live preview when empty-print matches
                                // export fill policy — skips a second docx-preview on "Xuất PDF trang".
                                // Layout edits are already on the live DOM; do not re-apply.
                                if (printEmptyWhenMissing) {
                                  try {
                                    // Inline blob:/http images so cached HTML stays printable after blob revoke.
                                    const { inlineDomImages } = await import("@/utils/printExporter");
                                    await inlineDomImages(previewRef.current);
                                    const snapped = snapshotDocxSections(previewRef.current, { detectPaper: true });
                                    // Cache WITHOUT overlays so overlay changes always apply fresh on export.
                                    renderCacheRef.current.set(exportRenderCacheKey(page.groupValue), stripTemplateOverlaysFromPages(snapped));
                                  } catch {
                                    // Preview may be a placeholder error card — ignore.
                                  }
                                }

                setSectionCount(sections.length);
        setVisibleSections(kept);
        setSkippedNotes(
          sections
            .map((_, i) => outcomes[pageIdOf(i)])
            .filter((o): o is PageConditionOutcome => Boolean(o) && !o!.applicable)
            .map((o) => o.reason)
        );
        setActiveSection((prev) => (kept.includes(prev) ? prev : kept[0] ?? 0));
        setOverflowSections(
          sections.map((el, i) => (isOverflowing(el) ? i : -1)).filter((i) => i >= 0)
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Render preview thất bại.";
      setUploadError(msg);
      // Keep the canvas informative: tell the user WHY it is empty and that
      // the page tabs above still work for duplicate/delete of template frames.
      if (previewRef.current) {
        const safe = msg.replace(/</g, "&lt;").replace(/\n/g, "<br/>");
        previewRef.current.innerHTML =
          '<div style="padding:32px;text-align:center;font-size:12px;color:#64748b;background:#fff;border-radius:8px;border:1px dashed #cbd5e1">' +
          "⚠ Không dựng được bản xem trước.<br/><br/><b style=\"color:#b45309\">" +
          safe +
          "</b><br/><br/>Template cần chứa placeholder <code>{{tên}}</code> để điền dữ liệu. " +
          'Bấm <b>Chỉnh Sửa Template</b> để thêm trường hoặc tải template khác.<br/>' +
          "Vẫn có thể <b>Nhân đôi / Xóa trang</b> ở dải trang phía trên.</div>";
      }
    } finally {
      setBusy(false);
    }
  }, [docxFile, bindBuffer, printPaper, addedPages, activePageId, fieldsWithColumns, fillFormats, layoutEdits, overlays, outcomesFor, pageIdOf, printEmptyWhenMissing, excelHash, templateHash]);

  // Drop stale filled-page cache when template/data/layout policy changes.
  React.useEffect(() => {
    renderCacheRef.current.clear();
  }, [excelHash, templateHash, printEmptyWhenMissing, layoutEdits, bindBuffer]);

  React.useEffect(() => {
    // While the editor is open the preview is unmounted; re-binding data on
    // close is what makes the edits show up without another click.
    if (step !== "preview" || templateEditorOpen) return;
    // When a split carton page is selected, show it instead of the normal page.
    if (activeSplitSubPage && previewRef.current) {
      previewRef.current.innerHTML = activeSplitSubPage.rendered.html;
      setSectionCount(1);
      setVisibleSections([0]);
      setActiveSection(0);
      return;
    }
    renderPreview();
  }, [step, templateEditorOpen, activeSplitSubPage, activePageId, addedPages.length, templateHash, bindBuffer, renderPreview]);

  // Show one physical page at a time, scaled by the zoom control.
  React.useEffect(() => {
    const container = previewRef.current;
    if (!container) return;
    const sections = Array.from(container.querySelectorAll("section.docx")) as HTMLElement[];
    sections.forEach((el, i) => {
      const applicable = visibleSections.length === 0 || visibleSections.includes(i);
      el.style.display = applicable && i === activeSection ? "block" : "none";
      el.style.transform = `scale(${zoom})`;
      el.style.transformOrigin = "top center";
    });
  }, [activeSection, zoom, sectionCount, activePageId, visibleSections]);

  async function fillGroup(group: DetailedGroup): Promise<ArrayBuffer | Uint8Array> {
      if (!docxFile) throw new Error("Chưa tải template.");
      return fillDocxTemplateWithMarkers(
        bindBuffer ?? docxFile.fileData,
        { rows: group.rows, headerValues: group.headerValues },
        {
          headerFields: fieldsWithColumns.filter((f) => !f.inRepeat),
          itemFields: fieldsWithColumns.filter((f) => f.inRepeat),
          formatByField: exportFormats,
          itemRowsBySection: rowsPerPage(group.rows),
        }
      );
    }

    /** Fingerprint for export-quality renders (blank-on-missing when setting on). */
    function exportRenderCacheKey(groupValue: string): string {
      return [
        "export",
        groupValue,
        excelHash,
        templateHash,
        printEmptyWhenMissing ? "empty1" : "empty0",
        // Hash the CONTENT of layout edits, not just their count: dragging an
        // edit to a new position (same length) must bust the cache, or export
        // serves a stale layout while the preview shows the moved element.
        hashBytes(new TextEncoder().encode(JSON.stringify(layoutEdits))),
        printPaper ? `${printPaper.paperSize}-${printPaper.orientation}` : "auto",
      ].join("|");
    }

    async function renderGroupPages(group: DetailedGroup): Promise<RenderedPage[]> {
      const key = exportRenderCacheKey(group.groupValue);
      let laid = renderCacheRef.current.get(key);
      if (!laid) {
        const filled = await fillGroup(group);
        const pages = await renderDocxToPages(filled, { detectPaper: true });
        laid = applyLayoutEditsToPages(pages, layoutEdits);
        // Cap cache so multi-thousand group jobs do not pin all HTML in memory.
        if (renderCacheRef.current.size > 80) {
          const first = renderCacheRef.current.keys().next().value;
          if (first != null) renderCacheRef.current.delete(first);
        }
        // Cache the layout-edited pages WITHOUT overlays so overlay changes
        // (add/move/edit) always apply fresh on export — overlays are cheap to
        // re-inject and must never be served stale from the cache.
        renderCacheRef.current.set(key, laid);
      }
      // Apply overlays after the cache lookup so they are always current.
      return applyTemplateOverlaysToPages(laid, overlays).map((p) => ({ ...p }));
    }

  // ── Session persistence ───────────────────────────────────
  const currentSessionKey = React.useMemo(
    () => (excelHash && templateHash ? sessionKey(excelHash, templateHash) : ""),
    [excelHash, templateHash]
  );

  // Offer to restore as soon as both files are known.
  React.useEffect(() => {
    if (!currentSessionKey) return;
    setRestorable(loadSession(currentSessionKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionKey]);

  // Auto-save whatever the user has configured so far.
  React.useEffect(() => {
    if (!currentSessionKey || restorable) return;
    saveSession(currentSessionKey, {
          step,
          selectedSheet,
          mappings: Object.fromEntries(
            Object.entries(mappings).map(([field, m]) => [field, m.excelColumn])
          ),
          formats,
          requiredOverrides,
          groupingColumns,
          addedPages: addedPages.map((p) => ({
            groupValue: p.groupValue,
            label: p.label,
            enabled: p.enabled,
          })),
          lookupSheet,
          joinKeyPrimary,
          joinKeySecondary,
          layoutEdits,
          overlays,
          printEmptyWhenMissing,
        });
      }, [
        currentSessionKey,
        restorable,
        step,
        selectedSheet,
        mappings,
        formats,
        requiredOverrides,
        groupingColumns,
        addedPages,
        lookupSheet,
        joinKeyPrimary,
        joinKeySecondary,
        layoutEdits,
        overlays,
        printEmptyWhenMissing,
      ]);

  // Persist template edits (layout + overlays) independently of the file pair,
  // so reopening the app restores the last template customization. Only save
  // when there is something to save, so loading a fresh template does not
  // overwrite the stored edits for that template.
  React.useEffect(() => {
    if (!templateHash) return;
    if (layoutEdits.length === 0 && overlays.length === 0) return;
    persistTemplateEdits(templateHash, layoutEdits, overlays);
  }, [templateHash, layoutEdits, overlays]);

  // Restore the last template edits when the loaded template matches the one
  // the edits were made against.
  React.useEffect(() => {
    if (!templateHash) return;
    const stored = loadTemplateEdits();
    if (!stored || stored.templateHash !== templateHash) return;
    if (stored.layoutEdits.length === 0 && stored.overlays.length === 0) return;
    setLayoutEdits(stored.layoutEdits);
    setOverlays(stored.overlays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateHash]);

  function restoreSession(session: SavedSession) {
    setSelectedSheet(session.selectedSheet);
    setFormats(session.formats ?? {});
    setRequiredOverrides(session.requiredOverrides ?? {});
    setLookupSheet(session.lookupSheet ?? "");
    setJoinKeyPrimary(session.joinKeyPrimary ?? "");
    setJoinKeySecondary(session.joinKeySecondary ?? "");
    setLayoutEdits(session.layoutEdits ?? []);
    setOverlays(session.overlays ?? []);
        setPrintEmptyWhenMissing(session.printEmptyWhenMissing !== false);
        setGroupingColumns((session.groupingColumns ?? []).slice(0, 1));
    setMappings(
      Object.fromEntries(
        Object.entries(session.mappings ?? {}).map(([field, column]) => [
          field,
          {
            fieldName: field,
            normalizedName: "",
            excelColumn: column,
            matchedColumns: column ? [column] : [],
            status: column ? ("mapped" as const) : ("not_found" as const),
          },
        ])
      )
    );
    // The export list is restored once the groups have been recomputed.
    pendingPagesRef.current = session.addedPages ?? [];
    setStep(session.step === "preview" ? "preview" : "data");
    setRestorable(null);
  }

  const pendingPagesRef = React.useRef<{ groupValue: string; label: string; enabled?: boolean }[]>([]);

  // Re-attach the restored export list to the freshly computed groups.
  React.useEffect(() => {
    if (pendingPagesRef.current.length === 0 || groups.length === 0) return;
    const byValue = new Map(groups.map((g) => [g.groupValue, g]));
    const restored: AddedPage[] = [];
    for (const saved of pendingPagesRef.current) {
      const group = byValue.get(saved.groupValue);
      if (group) {
        restored.push({
          id: safeUUID(),
          groupValue: saved.groupValue,
          label: saved.label,
          group,
          enabled: saved.enabled !== false,
        });
      }
    }
    pendingPagesRef.current = [];
    if (restored.length > 0) {
      setAddedPages(restored);
      setActivePageId(restored[0].id);
    }
  }, [groups]);

  /** Trace a clicked value on the preview back to its Excel cell. */
  function handlePreviewClick(event: React.MouseEvent<HTMLDivElement>) {
    const page = addedPages.find((p) => p.id === activePageId) ?? addedPages[0];
    if (!page) return;
    const text = (event.target as HTMLElement).textContent ?? "";
    const source = findValueSource(page.group.rows, text);
    setTraceInfo(
      source
        ? describeValueSource(source, joinKeyPrimary ? String(page.group.headerValues[joinKeyPrimary] ?? "") : undefined)
        : null
    );
  }

  /** Report pages whose content was taller than the paper (silently clipped). */
  function noteOverflow(pages: RenderedPage[]) {
    const clipped = pages
      .map((p, i) => (p.overflow ? i + 1 : 0))
      .filter((n) => n > 0);
    setExportNote(
      clipped.length > 0
        ? `Trang ${clipped.join(", ")} bị tràn nội dung — phần cuối trang có thể bị cắt.`
        : null
    );
  }

  /**
   * A rule pointing at a column or a page that no longer exists would filter on
   * the wrong thing, so exporting stops until the user fixes it.
   */
  function conditionsUsable(): boolean {
    const problems = [templateShapeWarning, ...conditionValidation.warnings].filter(Boolean);
    if (problems.length === 0) return true;
    setUploadError(`Điều kiện hiển thị trang cần cấu hình lại: ${problems.join(" ")}`);
    return false;
  }

  // ── Export / print: ONLY the user-added pages ─────────────
  /**
   * The pages a phiếu should output: when it has been split into cartons, only
   * the (enabled) carton copies are printed and the origin page is dropped so
   * it is not duplicated. Otherwise the normal rendered pages are used.
   */
  async function pagesForPhiếu(p: AddedPage): Promise<RenderedPage[]> {
    const cluster = splitClusters.find(
      (c) => c.enabled !== false && c.originPageId === p.id
    );
    if (cluster && cluster.subPages.length > 0) {
      return cluster.subPages.map((sp) => sp.rendered);
    }
    return keepApplicablePages(await renderGroupPages(p.group), p.group.rows);
  }

  async function exportAddedPagesPdf(scope: "active" | "all") {
    const pagesToExport = scope === "active" ? (activePage ? [activePage] : []) : enabledPages;
    if (pagesToExport.length === 0 && (scope !== "all" || enabledSplitSubPages.length === 0)) {
      toastWarning(
        "Chưa có trang để xuất",
        scope === "active"
          ? "Chọn một chứng từ trong danh sách bên phải."
          : "Tích ít nhất một chứng từ (hoặc phiếu tách kiện) trước khi xuất PDF."
      );
      return;
    }
    const preflight = runValidation(pagesToExport.length > 0 ? pagesToExport : enabledPages);
    if (!preflight.valid) {
      const errs = preflight.issues
        .filter((i) => i.severity === "error")
        .map((i) => i.message);
      toastError(
        "Không thể xuất PDF",
        errs.slice(0, 3).join(" · ") || "Hãy xử lý các lỗi trong danh sách bên dưới."
      );
      return;
    }
    if (!conditionsUsable()) return;
    setBusy(true);
    const kind: JobKind = scope === "active" ? "pdf-single" : "pdf-all";
    const jobId = `pdf-${Date.now().toString(36)}`;
    toastInfo(
      scope === "active" ? "Đang tạo PDF trang hiện tại…" : "Đang tạo PDF các trang đã chọn…",
      "Giữ tab mở đến khi tải xong."
    );
    // Record an in-flight job so Lịch sử in gần đây shows a live %.
    recordRecentJob({
      id: jobId,
      kind,
      excelFile: workbook?.sheets[0]?.name ?? "",
      templateFile: docxFile?.fileName ?? "",
      sheetName: selectedSheet,
      pageCount: 0,
      status: "ok",
      note: "Đang xử lý…",
      progress: 0,
    });
    try {
              // Parallel fill+render (bounded) — biggest win for multi-phiếu PDF/print.
              const batches = await mapPool(pagesToExport, 3, async (p) => pagesForPhiếu(p));
              const pages: RenderedPage[] = batches.flat();
              if (pages.length === 0) {
                updateRecentJobProgress(jobId, 100, { status: "error", note: "Không có trang để xuất" });
                const msg =
                  "Không còn trang nào thỏa điều kiện hiển thị cho các bản ghi đã chọn — hãy kiểm tra lại điều kiện trang.";
                setUploadError(msg);
                toastWarning("Không có trang để xuất", msg);
                return;
              }
              const overflowCount = pages.filter((p) => p.overflow).length;
              noteOverflow(pages);
              const numbered = applyPageNumbering(withPaper(pages, printPaper ?? undefined));
              // Do NOT pass renderScale — generatePDF auto-scales large batches (OOM guard).
              const blob = await generatePDF(numbered, {
                paper: printPaper ?? undefined,
                forcePaper: Boolean(printPaper),
                onProgress: (done, total) => {
                  updateRecentJobProgress(jobId, Math.round((done / Math.max(1, total)) * 100));
                },
              });
              if (!blob || blob.size < 64) {
            throw new Error(
              "PDF rỗng — không capture được nội dung trang. Thử Tải Lại xem trước rồi xuất lại."
            );
          }
          const fileName = `PhieuGiaoHang-${new Date().toISOString().slice(0, 10)}.pdf`;
          downloadBlob(blob, fileName);
          const note = overflowCount > 0 ? `${overflowCount} trang tràn` : undefined;
          toastSuccess(
            scope === "active" ? "Đã tải PDF trang" : `Đã tải PDF ${pages.length} trang`,
            note ?? fileName
          );
          recordRecentJob({
            id: jobId,
            kind,
            excelFile: workbook?.sheets[0]?.name ?? "",
            templateFile: docxFile?.fileName ?? "",
            sheetName: selectedSheet,
            pageCount: pages.length,
            paper: printPaper ? `${printPaper.paperSize} ${printPaper.orientation}` : undefined,
            status: "ok",
            note,
            progress: 100,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Xuất PDF thất bại.";
          setUploadError(msg);
          toastError("Xuất PDF thất bại", msg);
          recordRecentJob({
            id: jobId,
            kind,
            excelFile: workbook?.sheets[0]?.name ?? "",
            templateFile: docxFile?.fileName ?? "",
            sheetName: selectedSheet,
            pageCount: 0,
            status: "error",
            note: msg,
            progress: 100,
          });
        } finally {
          setBusy(false);
        }
      }

  async function handlePrintAddedPages() {
    if (enabledPages.length === 0 && enabledSplitSubPages.length === 0) return;
    const preflight = runValidation(enabledPages);
    if (!preflight.valid) {
      const errs = preflight.issues
        .filter((i) => i.severity === "error")
        .map((i) => i.message);
      toastError(
        "Không thể in",
        errs.slice(0, 3).join(" · ") || "Hãy xử lý các lỗi trong danh sách bên dưới."
      );
      return;
    }
    if (!conditionsUsable()) return;
    setBusy(true);
    const jobId = `print-${Date.now().toString(36)}`;
    toastInfo("Đang mở hộp in…");
    // Record an in-flight job so Lịch sử in gần đây shows a live %.
    recordRecentJob({
      id: jobId,
      kind: "print",
      excelFile: workbook?.sheets[0]?.name ?? "",
      templateFile: docxFile?.fileName ?? "",
      sheetName: selectedSheet,
      pageCount: 0,
      status: "ok",
      note: "Đang xử lý…",
      progress: 0,
    });
    let printed = 0;
    try {
          const batches = await mapPool(enabledPages, 3, async (p) => pagesForPhiếu(p));
          const pages: RenderedPage[] = batches.flat();
          if (pages.length === 0) {
            updateRecentJobProgress(jobId, 100, { status: "error", note: "Không có trang để in" });
            const msg =
              "Không còn trang nào thỏa điều kiện hiển thị cho các bản ghi đã chọn — hãy kiểm tra lại điều kiện trang.";
            setUploadError(msg);
            toastWarning("Không có trang để in", msg);
            return;
          }
          const overflowCount = pages.filter((p) => p.overflow).length;
          noteOverflow(pages);
          // Rendering done — show progress before the browser print dialog opens.
          updateRecentJobProgress(jobId, 90, { status: "ok", note: "Đang mở hộp in…" });
          await printDocument(
            applyPageNumbering(withPaper(pages, printPaper ?? undefined)),
            printPaper ?? undefined
          );
          printed = pages.length;
          toastSuccess(`Đã gửi ${pages.length} trang tới máy in`);
          updateRecentJobProgress(jobId, 100, {
            status: "ok",
            note: overflowCount > 0 ? `${overflowCount} trang tràn` : undefined,
          });
          recordRecentJob({
            id: jobId,
            kind: "print",
            excelFile: workbook?.sheets[0]?.name ?? "",
            templateFile: docxFile?.fileName ?? "",
            sheetName: selectedSheet,
            pageCount: pages.length,
            paper: printPaper ? `${printPaper.paperSize} ${printPaper.orientation}` : undefined,
            status: "ok",
            note: overflowCount > 0 ? `${overflowCount} trang tràn` : undefined,
            progress: 100,
          });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "In thất bại.";
      setUploadError(msg);
      toastError("In thất bại", msg);
      updateRecentJobProgress(jobId, 100, { status: "error", note: msg });
      recordRecentJob({
        id: jobId,
        kind: "print",
        excelFile: workbook?.sheets[0]?.name ?? "",
        templateFile: docxFile?.fileName ?? "",
        sheetName: selectedSheet,
        pageCount: printed,
        status: "error",
        note: msg,
        progress: 100,
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Build one PDF entry per selected phiếu. Each entry is a fully rendered,
   * condition-filtered page list for that single document, with any carton-split
   * copies of the phiếu appended right after it.
   */
  async function buildPerGroupPdfEntries(
      pagesToExport: AddedPage[]
    ): Promise<GroupedPdfEntry[]> {
      const built = await mapPool(pagesToExport, 3, async (p) => {
        const pages = await pagesForPhiếu(p);
        if (pages.length === 0) return null;
        return {
          fileName: p.label,
          pages: withPaper(pages, printPaper ?? undefined),
        } satisfies GroupedPdfEntry;
      });
      return built.filter((e): e is GroupedPdfEntry => e != null);
    }

  /**
   * Export every ticked phiếu as its own PDF, bundled into a single ZIP.
   * Unlike the merged export, page numbers are re-computed per document so each
   * phiếu reads "Trang 1/N … N/N" for itself.
   */
  async function exportAddedPagesZip() {
    if (enabledPages.length === 0 && enabledSplitSubPages.length === 0) {
      toastWarning(
        "Chưa có trang để xuất",
        "Tích ít nhất một chứng từ trước khi xuất ZIP."
      );
      return;
    }
    const preflight = runValidation(enabledPages);
    if (!preflight.valid) {
      const errs = preflight.issues
        .filter((i) => i.severity === "error")
        .map((i) => i.message);
      toastError(
        "Không thể xuất ZIP",
        errs.slice(0, 3).join(" · ") || "Hãy xử lý các lỗi trong danh sách bên dưới."
      );
      return;
    }
    if (!conditionsUsable()) return;
    setBusy(true);
    toastInfo("Đang tạo ZIP PDF…", "Giữ tab mở đến khi tải xong.");
    // Record an in-flight job so Lịch sử in gần đây shows a live %.
    const jobId = `zip-${Date.now().toString(36)}`;
    recordRecentJob({
      id: jobId,
      kind: "pdf-zip",
      excelFile: workbook?.sheets[0]?.name ?? "",
      templateFile: docxFile?.fileName ?? "",
      sheetName: selectedSheet,
      pageCount: 0,
      status: "ok",
      note: "Đang xử lý…",
      progress: 0,
    });
    try {
      const entries = await buildPerGroupPdfEntries(enabledPages);
      if (entries.length === 0) {
        updateRecentJobProgress(jobId, 100, { status: "error", note: "Không có phiếu để xuất" });
        setUploadError(
          "Không còn phiếu nào thỏa điều kiện hiển thị cho các bản ghi đã chọn — hãy kiểm tra lại điều kiện trang."
        );
        toastWarning("Không có phiếu để xuất ZIP");
        return;
      }
      let totalPages = 0;
      entries.forEach((e) => {
        totalPages += e.pages.length;
      });
      const overflowCount = entries.flatMap((e) => e.pages).filter((p) => p.overflow).length;
      noteOverflow(entries.flatMap((e) => e.pages));
      const { blob, fileName } = await exportPdfsAsZip(entries, {
        // Auto-scale inside zipper / generatePDF — do not force scale 3 (OOM).
        paper: printPaper ?? undefined,
        forcePaper: Boolean(printPaper),
        onProgress: (done, total) => {
          updateRecentJobProgress(jobId, Math.round((done / Math.max(1, total)) * 100));
        },
      });
      if (!blob || blob.size < 32) {
        throw new Error("ZIP rỗng — không tạo được file PDF trong gói.");
      }
      downloadBlob(blob, fileName);
      setExportNote(
        `Đã xuất ${entries.length} phiếu (${totalPages} trang) thành ${entries.length} file PDF trong 1 file ZIP.`
      );
      const note = overflowCount > 0 ? `${overflowCount} trang tràn` : undefined;
      toastSuccess(`Đã tải ZIP ${entries.length} phiếu`, note ?? fileName);
      updateRecentJobProgress(jobId, 100, { status: "ok", note });
      recordRecentJob({
        id: jobId,
        kind: "pdf-zip",
        excelFile: workbook?.sheets[0]?.name ?? "",
        templateFile: docxFile?.fileName ?? "",
        sheetName: selectedSheet,
        pageCount: totalPages,
        paper: printPaper ? `${printPaper.paperSize} ${printPaper.orientation}` : undefined,
        status: "ok",
        note,
        progress: 100,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Xuất từng phiếu (ZIP) thất bại.";
      setUploadError(msg);
      toastError("Xuất ZIP thất bại", msg);
      updateRecentJobProgress(jobId, 100, { status: "error", note: msg });
      recordRecentJob({
        id: jobId,
        kind: "pdf-zip",
        excelFile: workbook?.sheets[0]?.name ?? "",
        templateFile: docxFile?.fileName ?? "",
        sheetName: selectedSheet,
        pageCount: 0,
        status: "error",
        note: msg,
        progress: 100,
      });
    } finally {
      setBusy(false);
    }
  }

  const canProceed = docxFile !== null && headers.length > 0;
  const unmappedCount = Object.values(mappings).filter((m) => m.status !== "mapped").length;


  const runValidation = React.useCallback(
    (pages: AddedPage[]): ExportValidationResult => {
      const result = validateMarkerExport({
              hasTemplate: docxFile !== null,
              templateHasFieldMarkers: (docxFile?.fields.length ?? 0) > 0,
              templateErrors: docxFile?.errors,
              fields: uniqueFields.map((f) => ({
                fieldName: f.fieldName,
                inRepeat: f.inRepeat,
                excelColumn: f.excelColumn ?? "",
                required: isRequired(f),
              })),
              headers,
              rowCount: rows.length,
              selectedGroups: pages.map((p) => ({
                groupValue: p.groupValue,
                rows: p.group.rows,
                headerValues: p.group.headerValues,
              })),
              formats: exportFormats,
              printEmptyWhenMissing,
            });
            setValidation(result);
            // Preflight surface: even when export is allowed, warn about issues the
            // user should eyeball (unbound fields, layout overflows, etc).
            if (result.hasWarnings && !result.hasErrors) {
              const detail = result.issues
                .slice(0, 3)
                .map((i) => i.message)
                .join(" · ");
              toastWarning(
                `${result.issues.length} cảnh báo trước khi xuất`,
                detail || "Kiểm tra danh sách trang bên dưới trước khi in."
              );
            }
            return result;
          },
          [docxFile, uniqueFields, isRequired, headers, rows.length, exportFormats, printEmptyWhenMissing]
        );

  /**
   * Editor for the conditions of the page being previewed. Rendered inline
   * above the preview so the rule and the page it governs stay side by side.
   */
  const conditionPanel = (
    <div className="space-y-3">
      {(templateShapeWarning || conditionValidation.warnings.length > 0) && !isAlertDismissed("condition-config") && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-xs flex items-center justify-between">
            Cần cấu hình lại điều kiện
            <button type="button" onClick={() => dismissAlert("condition-config")} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </AlertTitle>
          <AlertDescription className="text-xs">
            <ul className="mt-1 space-y-0.5">
              {templateShapeWarning && <li>{templateShapeWarning}</li>}
              {conditionValidation.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox
            checked={conditionRule?.enabled ?? false}
            disabled={(conditionRule?.conditions.length ?? 0) === 0}
            onCheckedChange={(v) =>
              updateRule(conditionPageId, (rule) => ({ ...rule, enabled: Boolean(v) }))
            }
          />
          Bật điều kiện
        </label>

        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox
            checked={conditionRule?.filterRows !== false}
            disabled={(conditionRule?.conditions.length ?? 0) === 0}
            onCheckedChange={(v) =>
              updateRule(conditionPageId, (rule) => ({ ...rule, filterRows: Boolean(v) }))
            }
          />
          Chỉ liệt kê dòng thoả điều kiện trong bảng chi tiết
        </label>

        {(conditionRule?.conditions.length ?? 0) > 1 && (
          <Select
            value={conditionRule?.combinator ?? "AND"}
            onValueChange={(v) =>
              updateRule(conditionPageId, (rule) => ({
                ...rule,
                combinator: v === "OR" ? "OR" : "AND",
              }))
            }
          >
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">Thoả TẤT CẢ (AND)</SelectItem>
              <SelectItem value="OR">Thoả BẤT KỲ (OR)</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {(conditionRule?.conditions.length ?? 0) === 0 ? (
        <p className="text-xs text-muted-foreground">
          Chưa có điều kiện — trang luôn được in cho mọi bản ghi.
        </p>
      ) : (
        <div className="space-y-2">
          {conditionRule!.conditions.map((c, index) => (
            <div
              key={c.id}
              className={cn(
                "rounded-lg border bg-background p-2 space-y-2",
                !isConditionComplete(c) && "border-amber-400 bg-amber-50/50"
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {index === 0 ? "Nếu" : conditionRule!.combinator === "AND" ? "VÀ" : "HOẶC"}
                </Badge>
                <Select
                  value={c.field || NO_MAPPING}
                  onValueChange={(v) =>
                    patchCondition(conditionPageId, c.id, {
                      field: v === NO_MAPPING ? "" : v,
                      values: [],
                    })
                  }
                >
                  <SelectTrigger className="h-8 w-48 text-xs">
                    <SelectValue placeholder="Chọn cột" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_MAPPING}>-- Chọn cột --</SelectItem>
                    {headers.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={c.operator}
                  onValueChange={(v) =>
                    patchCondition(conditionPageId, c.id, { operator: v as PageConditionOperator })
                  }
                >
                  <SelectTrigger className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...ROW_OPERATORS, ...AGGREGATE_OPERATORS].map((op) => (
                      <SelectItem key={op} value={op}>
                        {isAggregateOperator(op)
                          ? `Tổng của cột ${OPERATOR_LABELS[op].replace("tổng ", "")}`
                          : OPERATOR_LABELS[op]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <button
                  className="ml-auto text-muted-foreground hover:text-red-600"
                  title="Xoá điều kiện"
                  onClick={() => removeCondition(conditionPageId, c.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {VALUELESS_OPERATORS.includes(c.operator) ? (
                <p className="text-[11px] text-muted-foreground">Toán tử này không cần giá trị.</p>
              ) : NUMERIC_OPERATORS.includes(c.operator) ? (
                <Input
                  type="number"
                  className="h-8 w-40 text-xs"
                  placeholder="Giá trị ngưỡng"
                  value={c.threshold ?? ""}
                  onChange={(e) =>
                    patchCondition(conditionPageId, c.id, {
                      threshold: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                />
              ) : c.field ? (
                <ConditionValuePicker
                  options={distinctValues(rows, c.field)}
                  selected={c.values}
                  onChange={(values) => patchCondition(conditionPageId, c.id, { values })}
                />
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Chọn cột để lấy danh sách giá trị đang có.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          onClick={() => addCondition(conditionPageId)}
          disabled={headers.length === 0}
        >
          <Plus className="h-3.5 w-3.5" /> Thêm điều kiện
        </Button>
        {(conditionRule?.conditions.length ?? 0) > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1.5 text-xs"
            onClick={() => clearPageConditions(conditionPageId)}
          >
            <X className="h-3.5 w-3.5" /> Xoá toàn bộ điều kiện của trang này
          </Button>
        )}
      </div>
    </div>
  );

  return (
      <div className={cn("w-full space-y-3", className)}>
        {/* Enterprise stepper — full-width segments, done / active / pending */}
        <nav className="ent-stepper" aria-label="Tiến trình wizard">
          {STEPS.map((s, i) => {
            const currentIdx = STEPS.findIndex((st) => st.key === step);
            const done = i < currentIdx;
            const current = step === s.key;
            return (
              <React.Fragment key={s.key}>
                <button
                  type="button"
                  onClick={() => setStep(s.key)}
                  aria-current={current ? "step" : undefined}
                  className={cn(
                    "relative flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-left text-xs font-semibold transition-colors sm:px-4",
                    current && "bg-primary text-primary-foreground",
                    done && !current && "bg-emerald-50 text-emerald-800 hover:bg-emerald-100/90",
                    !done && !current && "bg-card text-muted-foreground hover:bg-muted/60"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ring-1",
                      current && "bg-primary-foreground/15 text-primary-foreground ring-primary-foreground/25",
                      done && !current && "bg-emerald-600 text-white ring-emerald-700/20",
                      !done && !current && "bg-muted text-muted-foreground ring-border"
                    )}
                  >
                    {done ? "✓" : i + 1}
                  </span>
                  <span className="min-w-0">
                    <span className={cn("block truncate leading-tight", current && "text-primary-foreground")}>
                      {s.label.replace(/^\d+\.\s*/, "")}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 block text-[10px] font-medium leading-none opacity-75",
                        current ? "text-primary-foreground/80" : "text-muted-foreground"
                      )}
                    >
                      {done ? "Hoàn tất" : current ? "Đang làm" : "Chờ"}
                    </span>
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div
                    className="hidden w-px self-stretch bg-border sm:block"
                    aria-hidden
                  />
                )}
              </React.Fragment>
            );
          })}
        </nav>

      {restorable && !isAlertDismissed("restore-session") && (
        <Alert className="border-primary/40 bg-primary/5">
          <FileSearch className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between">
            Khôi phục phiên làm việc trước?
            <button type="button" onClick={() => dismissAlert("restore-session")} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </AlertTitle>
          <AlertDescription className="text-xs">
            <p>
              Tìm thấy cấu hình đã lưu cho đúng cặp file này (
              {new Date(restorable.savedAt).toLocaleString("vi-VN")}): mapping, cột gom nhóm, danh
              sách in và chỉnh sửa layout.
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => restoreSession(restorable)}>
                Khôi phục
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  clearSession(currentSessionKey);
                  setRestorable(null);
                }}
              >
                Bắt đầu mới
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {uploadError && !isAlertDismissed("upload-error") && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between">
            Lỗi
            <button type="button" onClick={() => dismissAlert("upload-error")} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </AlertTitle>
          <AlertDescription className="text-xs whitespace-pre-line">{uploadError}</AlertDescription>
        </Alert>
      )}

      {step === "data" && (
        <SectionErrorBoundary label="Bước Dữ Liệu">
          <DataStep
            busy={busy}
            workbook={workbook}
            workbookRoleSummary={workbookRoleSummary}
            isAlertDismissed={isAlertDismissed}
            dismissAlert={dismissAlert}
            selectedSheet={selectedSheet}
            setSelectedSheet={setSelectedSheet}
            headers={headers}
            rows={rows}
            groupingColumns={groupingColumns}
            setGroupingColumns={setGroupingColumns}
            setGroupSearch={setGroupSearch}
            setPrintFilterMode={setPrintFilterMode}
            primaryHeaders={primaryHeaders}
            enabledPages={enabledPages}
            groups={groups}
            lookupSheet={lookupSheet}
            setLookupSheet={setLookupSheet}
            joinKeyPrimary={joinKeyPrimary}
            setJoinKeyPrimary={setJoinKeyPrimary}
            joinKeySecondary={joinKeySecondary}
            setJoinKeySecondary={setJoinKeySecondary}
            secondarySheet={secondarySheet}
            joinKeyCandidates={joinKeyCandidates}
            joined={joined}
            joinWarningMessages={joinWarningMessages}
            onExcelUpload={handleExcelUpload}
            onContinue={() => {
              // KHÔNG tự load template default — user chọn mẫu ở bước 2 (Xem Trước & In).
              setStep("preview");
            }}
          />
        </SectionErrorBoundary>
      )}

      {/* Template editor: opened on demand from the preview step, so the user
          edits the template right next to the result it produces. */}
      {step === "preview" && templateEditorOpen && docxFile && (
        <TemplateReviewStep
          docxFile={docxFile}
          headers={headers}
          primarySheetName={selectedSheet}
          secondarySheetName={lookupSheet}
          joinedAddedColumns={joined.addedColumns}
          templatePreviewRef={templatePreviewRef}
          busy={busy}
          templateEditMode={templateEditMode}
          onRenderTemplatePreview={renderTemplatePreview}
          onToggleTemplateEdit={toggleTemplateEdit}
          onSaveTemplateEdits={saveTemplateEdits}
          onDownloadTemplate={handleDownloadCurrentTemplate}
          onDuplicateSection={(idx) => duplicateSectionAt(idx)}
          onDeleteSection={(idx) => deleteSectionAt(idx)}
          canProceed={canProceed}
          backLabel="Đóng, Không Lưu"
          proceedLabel="Lưu & Xem Kết Quả"
          onBackToUpload={closeTemplateEditor}
          onProceedToPreview={saveAndCloseTemplateEditor}
          onActivePageChange={(pageIndex) => setConditionPageId(pageIndex)}
          initialActivePage={conditionPageId}
          splitField={splitField}
          onSplitFieldChange={setSplitField}
          numericColumns={numericColumns}
          overlays={overlays}
          onOverlaysChange={setOverlays}
          formats={formats}
          onFormatsChange={setFormats}
          onRestoreCanvasSnapshot={restoreTemplateCanvasSnapshot}
          conditionBar={
            <div className="rounded-lg border bg-muted/30">
              <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                <Filter className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold">
                  Điều kiện hiển thị
                </span>
                {incompleteConditions.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-amber-600 cursor-help">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          <span className="text-xs font-medium">
                            {incompleteConditions.length} chưa đủ thông tin
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs whitespace-normal text-xs" side="bottom">
                        <p className="font-semibold mb-1">
                          {incompleteConditions.length} điều kiện chưa đủ thông tin — đang bị bỏ qua
                        </p>
                        <p className="text-muted-foreground">
                          Điều kiện chỉ có hiệu lực khi đã chọn cột và chọn giá trị (hoặc nhập ngưỡng số).
                          Chừng nào còn thiếu, trang vẫn được in như bình thường.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <span className="text-xs text-muted-foreground">
                  {(conditionRule?.conditions.length ?? 0) > 0
                    ? describeRule(conditionRule!) || "Điều kiện chưa đủ thông tin"
                    : null}
                </span>
                <Button
                  size="sm"
                  variant={conditionPanelOpen ? "secondary" : "outline"}
                  className="ml-auto h-7 gap-1.5 px-2.5 text-xs"
                  onClick={() => setConditionPanelOpen((open) => !open)}
                >
                  {conditionPanelOpen ? (
                    <>
                      <ChevronUp className="h-3.5 w-3.5" /> Thu gọn
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3.5 w-3.5" />
                      {(conditionRule?.conditions.length ?? 0) === 0
                        ? "Đặt điều kiện"
                        : "Sửa điều kiện"}
                    </>
                  )}
                </Button>
              </div>
              {conditionPanelOpen && <div className="border-t px-3 py-3">{conditionPanel}</div>}
            </div>
          }
        />
      )}

      {step === "preview" && !templateEditorOpen && (
              <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-12">
                {/* Sticky context bar — always shows what will print */}
                <PreviewContextBar
                  onBack={() => setStep("data")}
                  excelFileName={excelFileName}
                  selectedSheet={selectedSheet}
                  templateName={docxFile?.fileName ?? null}
                  onOpenTemplatePicker={() => setPresetPickerOpen(true)}
                  printPaper={printPaper}
                  onOpenProfileEditor={() => setProfileEditorOpen(true)}
                  enabledPageCount={enabledPages.length + enabledSplitSubPages.length}
                  previewPanelOpen={previewPanelOpen}
                  onTogglePanel={() => setPreviewPanelOpen((v) => !v)}
                />
          {validation && validation.issues.length > 0 && !isAlertDismissed("validation") && (
                      <Alert
                        variant={validation.hasErrors ? "destructive" : "default"}
                        className={cn("lg:col-span-12", !validation.hasErrors && "border-amber-400 bg-amber-50")}
                      >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="flex items-center justify-between">
                {validation.hasErrors
                  ? "Không thể xuất — hãy xử lý các lỗi sau"
                  : "Cảnh báo trước khi xuất"}
                <button type="button" onClick={() => dismissAlert("validation")} className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </AlertTitle>
              <AlertDescription>
                <ul className="mt-1 space-y-1 text-xs">
                  {validation.issues.map((issue) => (
                    <li key={issue.id}>
                      <span className="font-semibold">
                        {issue.severity === "error" ? "Lỗi" : "Cảnh báo"} · {issue.title}:
                      </span>{" "}
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {exportNote && !isAlertDismissed("export-note") && (
                      <Alert className="lg:col-span-12 border-amber-400 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 flex items-center justify-between">
                Cảnh báo tràn trang
                <button type="button" onClick={() => dismissAlert("export-note")} className="text-amber-600 hover:text-amber-800">
                  <X className="h-4 w-4" />
                </button>
              </AlertTitle>
              <AlertDescription className="text-amber-700 text-xs">{exportNote}</AlertDescription>
            </Alert>
          )}

          <div className={cn("lg:col-span-8 space-y-3 min-w-0", !previewPanelOpen && "lg:col-span-12")}>
                      <Card className="shadow-sm overflow-hidden">
                        <CardHeader className="space-y-0 border-b bg-muted/30 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              {activePage ? (
                                <>
                                  Xem Trước ·{" "}
                                  <span className="font-normal text-muted-foreground">
                                    {addedPages.findIndex((p) => p.id === activePage.id) + 1}/{addedPages.length}
                                  </span>
                                  <span className="max-w-[200px] truncate font-medium" title={activePage.label}>
                                    {activePage.label}
                                  </span>
                                </>
                              ) : (
                                <>Xem Trước</>
                              )}
                            </CardTitle>
                            <div className="flex items-center gap-1">
                              {/* Reload */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setActiveSplitPageId("");
                                  renderPreview();
                                }}
                                disabled={busy}
                                title="Dựng lại bản xem trước từ template đã bind"
                                className="h-7 px-1.5 text-xs"
                              >
                                <RefreshCw className="h-3.5 w-3.5" /> Tải Lại
                              </Button>
                              {/* Zoom */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setZoom((z) => Math.max(0.4, Math.round((z - 0.1) * 10) / 10))}
                                title="Thu nhỏ"
                                className="h-7 w-7 p-0"
                              >
                                <ZoomOut className="h-3.5 w-3.5" />
                              </Button>
                              <span className="w-11 text-center text-xs tabular-nums">
                                {Math.round(zoom * 100)}%
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 10) / 10))}
                                title="Phóng to"
                                className="h-7 w-7 p-0"
                              >
                                <ZoomIn className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-1.5 text-xs" onClick={() => setZoom(1)} title="Về 100%">
                                100%
                              </Button>
                              <span className="mx-0.5 h-4 w-px bg-border" />
                              <Button
                                size="sm"
                                onClick={() => openTemplateEditor(pageIdOf(activeSection))}
                                disabled={!docxFile || busy}
                                className="h-8 gap-1.5 text-xs font-semibold"
                                title="Mở canvas Word tại khung trang đang xem, lưu xong dữ liệu tự bind lại"
                              >
                                <Pencil className="h-3.5 w-3.5" /> Chỉnh Sửa Template
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          {!docxFile ? (
                            <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm">
                              <FolderOpen className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                              <p className="font-medium text-foreground">Chưa chọn template</p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Chọn mẫu có sẵn hoặc tải file <code className="rounded bg-muted px-1">.docx</code> (chứa placeholder{" "}
                                <code className="rounded bg-muted px-1">{"{{tên}}"}</code>) để bắt đầu in.
                              </p>
                              <Button
                                size="sm"
                                className="mt-3"
                                onClick={() => setPresetPickerOpen(true)}
                                disabled={busy}
                              >
                                <FolderOpen className="h-4 w-4" /> Chọn Template
                              </Button>
                            </div>
                          ) : addedPages.length === 0 ? (
                            <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                              Chưa có trang nào. Ở cột bên phải: chọn cột in theo (ví dụ{" "}
                              <code className="rounded bg-muted px-1">Mã điểm giao</code>) để tạo danh sách các trang.
                              <br />
                              <span className="text-xs">Chỉ những trang được tích mới được in / xuất PDF.</span>
                            </div>
                          ) : (
                            <>
                              {/* Toolbar — current group summary */}
                              <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                                <Badge variant="outline" className="gap-1 px-2 py-0.5 font-normal">
                                  <FileText className="h-3 w-3" />
                                  {activePage?.label ?? "—"}
                                </Badge>
                                {activePage && (
                                  <span>{activePage.group.totalItems} dòng chi tiết</span>
                                )}
                              </div>

                    {(() => {
                                          // Preview tabs are view-only. Edit / duplicate / delete live
                                          // exclusively in "Chỉnh Sửa Template".
                                          const fromInfo = sectionInfo?.sections.map((s) => s.index) ?? [];
                                          const tabIndices =
                                            fromInfo.length > visibleSections.length
                                              ? fromInfo
                                              : visibleSections.length > 0
                                                ? visibleSections
                                                : fromInfo;
                                          if (tabIndices.length === 0) return null;
                                          return (
                                            <div className="mb-2 flex flex-wrap items-center gap-1.5">
                                              <span className="text-xs text-muted-foreground">Trang của bộ chứng từ:</span>
                                              {tabIndices.map((sectionIndex, position) => {
                                                const rule = pageConditions[String(pageIdOf(sectionIndex))];
                                                const hasRule =
                                                  (rule?.conditions.length ?? 0) > 0 && rule?.enabled !== false;
                                                const isActive = sectionIndex === activeSection;
                                                const pageId = pageIdOf(sectionIndex);
                                                return (
                                                  <Button
                                                    key={sectionIndex}
                                                    size="sm"
                                                    variant={isActive ? "default" : "outline"}
                                                    className="h-7 gap-1 px-2.5 text-xs"
                                                    title={
                                                      hasRule
                                                        ? `Trang có điều kiện hiển thị: ${describeRule(rule!)}`
                                                        : "Xem trang này"
                                                    }
                                                    onClick={() => {
                                                      setActiveSection(sectionIndex);
                                                      setConditionPageId(pageId);
                                                      setActiveSplitPageId("");
                                                    }}
                                                  >
                                                    {hasRule && <Filter className="h-3 w-3" />}
                                                    Trang {position + 1}
                                                    {overflowSections.includes(sectionIndex) && " ⚠"}
                                                  </Button>
                                                );
                                              })}
                                              <span className="ml-1 text-[11px] text-muted-foreground">
                                                Chỉ xem · Sửa / Nhân đôi / Xóa ở{" "}
                                                <button
                                                  type="button"
                                                  className="font-medium text-primary underline-offset-2 hover:underline"
                                                  onClick={() => openTemplateEditor(activeSection)}
                                                >
                                                  Chỉnh Sửa Template
                                                </button>
                                              </span>
                                            </div>
                                          );
                                        })()}

                    {/* Condition summary bar: only when a rule is set */}
                    {(conditionRule?.conditions.length ?? 0) > 0 && (
                    <div className="mb-2 rounded-lg border bg-muted/30 px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
                      <Filter className="h-4 w-4 text-primary" />
                      <span className="font-semibold">
                        Điều kiện:
                      </span>
                      <span className="text-muted-foreground">
                        {describeRule(conditionRule!) || "Điều kiện chưa đủ thông tin"}
                      </span>
                      {activePage &&
                        (conditionRule?.conditions.length ?? 0) > 0 &&
                        conditionRule?.filterRows !== false && (
                          <Badge variant="secondary" className="text-[10px] ml-auto">
                            liệt kê {rowsPerPage(activePage.group.rows)[conditionPageId]?.length ?? 0}/
                            {activePage.group.rows.length} dòng
                          </Badge>
                        )}
                    </div>
                    )}

                    {activePage && activeOutcomes[pageIdOf(activeSection)] && (
                      <div
                        className={cn(
                          "mb-2 rounded-lg border px-2.5 py-1.5 text-xs",
                          activeOutcomes[pageIdOf(activeSection)].applicable
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : "border-slate-300 bg-slate-50 text-slate-700"
                        )}
                      >
                        Trang {pageIdOf(activeSection) + 1}{" "}
                        <strong>
                          {activeOutcomes[pageIdOf(activeSection)].applicable
                            ? "được in"
                            : "không áp dụng"}
                        </strong>{" "}
                        cho bản ghi "{activePage.label}".
                      </div>
                    )}

                    {skippedNotes.length > 0 && (
                      <Alert className="mb-2 border-slate-300 bg-slate-50">
                        <EyeOff className="h-4 w-4 text-slate-600" />
                        <AlertTitle className="text-slate-800 text-xs">
                          {skippedNotes.length} trang không áp dụng cho bản ghi này
                        </AlertTitle>
                        <AlertDescription className="text-slate-700 text-xs">
                          <ul className="mt-1 space-y-0.5">
                            {skippedNotes.map((note) => (
                              <li key={note}>{note}</li>
                            ))}
                          </ul>
                          Các trang này không được đánh số, không tính vào tổng và không xuất hiện
                          trong PDF / lệnh in.
                        </AlertDescription>
                      </Alert>
                    )}

                    {overflowSections.includes(activeSection) && (
                      <Alert className="mb-2 border-amber-400 bg-amber-50">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertTitle className="text-amber-800">Trang này bị tràn nội dung</AlertTitle>
                        <AlertDescription className="text-amber-700 text-xs">
                          Nội dung cao hơn khổ giấy nên phần cuối sẽ bị cắt khi in. Hãy giảm số dòng
                          trong nhóm hoặc chỉnh lại template.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div
                                          ref={previewRef}
                                          onClick={handlePreviewClick}
                                          className="max-h-[70vh] overflow-auto rounded-lg border border-border/60 bg-[hsl(210_16%_94%)] p-3 shadow-inner"
                                        />

                    <p className="mt-2 text-xs text-muted-foreground">
                      {traceInfo ?? "Bấm vào một giá trị trên trang để xem nó đến từ dòng Excel nào."}
                    </p>
                  </>
                )}
              </CardContent>
                          </Card>

                          {/* Auto-mapping — optional: auto-map runs on its own, expand only
                              when a field needs a manual fix. */}
                          <Card className="shadow-sm overflow-hidden">
                            <button
                              type="button"
                              className="flex w-full items-center justify-between gap-2 border-b border-transparent px-3 py-2.5 text-left hover:bg-muted/30 data-[open=true]:border-border data-[open=true]:bg-muted/20"
                              onClick={() => setMappingPanelOpen((v) => !v)}
                              aria-expanded={mappingPanelOpen}
                              data-open={mappingPanelOpen || undefined}
                            >
                              <span className="flex items-center gap-2 text-sm font-semibold">
                                <ListChecks className="h-4 w-4 text-primary" /> Ánh Xạ Trường
                                <span className="text-[10px] font-normal text-muted-foreground">(tự động)</span>
                              </span>
                              <span className="flex items-center gap-2">
                                {docxFile && uniqueFields.length > 0 && (
                                  <Badge
                                    variant={unmappedCount > 0 ? "outline" : "secondary"}
                                    className={cn("text-[10px]", unmappedCount > 0 && "border-amber-400 text-amber-700")}
                                  >
                                    {uniqueFields.length - unmappedCount}/{uniqueFields.length} đã map
                                    {unmappedCount > 0 && ` · ${unmappedCount} chưa khớp`}
                                  </Badge>
                                )}
                                <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", mappingPanelOpen && "rotate-180")} />
                              </span>
                            </button>
                            {mappingPanelOpen && (
                              <div className="border-t px-3 py-2.5">
                                {!docxFile ? (
                                  <p className="text-xs text-muted-foreground">Chưa có template.</p>
                                ) : uniqueFields.length === 0 ? (
                                  <p className="text-[11px] text-muted-foreground">
                                    Template chưa có placeholder nào.
                                  </p>
                                ) : (
                                  <div className="space-y-1.5">
                                    <div className="max-h-56 overflow-auto space-y-1 rounded-lg border p-2">
                                      {uniqueFields.map((f) => {
                                        const m = mappings[f.fieldName];
                                        const col = m?.status === "mapped" ? m.excelColumn : "";
                                        return (
                                          <div
                                            key={f.fieldName}
                                            className="flex items-center justify-between gap-2 text-[11px]"
                                          >
                                            <span className="font-mono truncate">{f.fieldName}</span>
                                            {col ? (
                                              <Badge variant="secondary" className="shrink-0 text-[10px]">
                                                → {col}
                                              </Badge>
                                            ) : (
                                              <Badge
                                                variant="outline"
                                                className="shrink-0 text-[10px] border-dashed"
                                              >
                                                chưa match
                                              </Badge>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {unmappedCount > 0 && (
                                      <p className="text-[11px] text-amber-600">
                                        {unmappedCount} trường chưa khớp tên cột Excel —{" "}
                                        {printEmptyWhenMissing ? (
                                          <>
                                            sẽ <strong>in rỗng</strong> (đang bật «Thiếu data → in rỗng»).
                                          </>
                                        ) : (
                                          <>
                                            sẽ hiện <code>{`{{tên}}`}</code> trên bản xem trước.
                                          </>
                                        )}{" "}
                                        Đổi tên placeholder trong{" "}
                                        <button
                                          type="button"
                                          className="font-semibold text-primary underline underline-offset-2"
                                          onClick={() => openTemplateEditor(pageIdOf(activeSection))}
                                        >
                                          Chỉnh Sửa Template
                                        </button>{" "}
                                        để khớp cột.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </Card>
                        </div>

                        <div className={cn("lg:col-span-4 ent-sticky-rail space-y-3 min-w-0", !previewPanelOpen && "hidden")}>
                          {/* Merged print list: tick = will be printed (filter over data2) */}
                          <div data-tour="tour-print-list">
                          <PrintListFilterCard
                          groupsCount={groups.length}
                          groupingColumns={groupingColumns}
                          filterColumns={printFilterColumns}
                          pages={addedPages}
                          visiblePages={visiblePrintPages}
                          enabledCount={enabledPages.length}
                          activePageId={activePage?.id ?? null}
                          groupSearch={groupSearch}
                          onGroupSearchChange={setGroupSearch}
                          columnFilters={columnFilters}
                          onColumnFiltersChange={setColumnFilters}
                          columnFilterOpen={columnFilterOpen}
                          onColumnFilterOpenChange={setColumnFilterOpen}
                          listExtraColumns={listExtraColumns}
                          onListExtraColumnsChange={setListExtraColumns}
                          printFilterMode={printFilterMode}
                          onPrintFilterModeChange={setPrintFilterMode}
                          editingPageId={editingPageId}
                          onEditingPageIdChange={setEditingPageId}
                          onSelectPage={setActivePageId}
                          onToggleEnabled={toggleEnabled}
                          onSetAllEnabled={setAllEnabled}
                          onMovePage={handleMovePage}
                          onRenamePage={handleRenamePage}
                          onGoToDataStep={() => setStep("data")}
                        />
                          </div>

                        {/* Export / print (only ticked pages) */}
                        <Card className="shadow-sm overflow-hidden border-primary/15" data-tour="tour-export-actions">
                          <CardHeader className="space-y-0 border-b bg-primary/5 px-3 py-2.5">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <button
                                type="button"
                                className="flex items-center gap-1.5 text-left"
                                onClick={() => setExportCardOpen((v) => !v)}
                                title={exportCardOpen ? "Thu gọn" : "Mở rộng"}
                              >
                                <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", exportCardOpen && "rotate-90")} />
                                <Printer className="h-4 w-4 shrink-0 text-primary" />
                                <span>Xuất & In</span>
                              </button>
                            </CardTitle>
                            {exportCardOpen && (
                            <CardDescription className="pt-0.5 text-[11px]">
                              In = hành động chính · PDF/ZIP phía dưới · khổ giấy ở bước Mẫu Template
                            </CardDescription>
                            )}
                          </CardHeader>
                          {exportCardOpen && (
                          <CardContent className="space-y-2 p-3">
                            <label
                              className={cn(
                                "flex cursor-pointer items-start gap-2 rounded-lg border px-2.5 py-2 text-xs transition-colors",
                                printEmptyWhenMissing
                                  ? "border-primary/40 bg-primary/5"
                                  : "border-border bg-muted/20 hover:bg-muted/40"
                              )}
                            >
                              <Checkbox
                                className="mt-0.5"
                                checked={printEmptyWhenMissing}
                                onCheckedChange={(v) => setPrintEmptyWhenMissing(Boolean(v))}
                                aria-label="Thiếu dữ liệu thì in rỗng"
                              />
                              <span className="min-w-0 space-y-0.5">
                                <span className="block font-medium leading-snug">
                                  Thiếu data → in rỗng
                                </span>
                                <span className="block text-[11px] leading-snug text-muted-foreground">
                                  Ô trống / chưa map in trống. Tắt để xem placeholder trên xem trước.
                                </span>
                              </span>
                            </label>

                            <div className="rounded-md border border-border/70 bg-muted/20 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                              Đã chọn{" "}
                              <strong className="text-foreground tabular-nums">
                                {enabledPages.length + enabledSplitSubPages.length}
                              </strong>{" "}
                              trang
                            </div>

                            <Button
                              className="h-11 w-full gap-2 text-sm font-semibold shadow-sm"
                              onClick={handlePrintAddedPages}
                              disabled={busy || enabledPages.length + enabledSplitSubPages.length === 0}
                              title="Gửi các trang đã tích tới máy in (hộp thoại in của trình duyệt)"
                            >
                              <Printer className="h-4 w-4" />{" "}
                              In {enabledPages.length + enabledSplitSubPages.length} Trang Đã Chọn
                            </Button>

                            <p className="ent-section-label pt-1">Tải file</p>
                            <Button
                              className="w-full gap-2"
                              size="sm"
                              variant="outline"
                              onClick={() => exportAddedPagesPdf("all")}
                              disabled={busy || enabledPages.length + enabledSplitSubPages.length === 0}
                            >
                              <Download className="h-4 w-4" /> Xuất PDF{" "}
                              {enabledPages.length + enabledSplitSubPages.length} Trang
                            </Button>
                            <Button
                              className="w-full gap-2"
                              size="sm"
                              variant="outline"
                              onClick={() => exportAddedPagesPdf("active")}
                              disabled={busy || !activePage}
                            >
                              <Download className="h-4 w-4" /> PDF Trang Hiện Tại
                            </Button>
                            <Button
                              className="w-full gap-2"
                              size="sm"
                              variant="secondary"
                              onClick={exportAddedPagesZip}
                              disabled={busy || enabledPages.length + enabledSplitSubPages.length === 0}
                              title="Mỗi phiếu thành 1 file PDF riêng, đóng gói trong 1 file ZIP."
                            >
                              <Archive className="h-4 w-4" /> ZIP từng phiếu ({enabledPages.length})
                            </Button>
                          </CardContent>
                          )}
                        </Card>
                      </div>
                    </div>
                  )}

                  {busy && (
                    <div
                      className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="rounded-full border border-border/80 bg-background/95 px-4 py-1.5 text-xs font-medium text-foreground shadow-md backdrop-blur">
                        Đang xử lý…
                      </div>
                    </div>
                  )}

                  {/* Preset template picker dialog */}
                  <PresetTemplatePicker
                    open={presetPickerOpen}
                    onOpenChange={setPresetPickerOpen}
                    onSelect={(preset) => {
                      setPresetPickerOpen(false);
                      void handleUsePresetTemplate(preset);
                    }}
                    onSelectUserTemplate={(tpl) => {
                      setPresetPickerOpen(false);
                      void handleUseUserTemplate(tpl);
                    }}
                    onRenameTemplate={(oldName, newName) => {
                      if (docxFile?.fileName === oldName) {
                        setDocxFile((prev) => (prev ? { ...prev, fileName: newName } : prev));
                      }
                    }}
                    editedTemplateNames={
                      layoutEdits.length > 0 || overlays.length > 0
                        ? [docxFile?.fileName || ""]
                        : []
                    }
                    busy={busy}
                  />

                  {/* Paper/printer profile editor — opened from the preview step */}
                  <Dialog open={profileEditorOpen} onOpenChange={setProfileEditorOpen}>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-base">Khổ Giấy & Máy In</DialogTitle>
                        <DialogDescription className="text-xs">
                          Chọn hồ sơ máy in hoặc đổi khổ giấy. Template được đổi khổ thật để bố cục vừa trang.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <PrinterProfilesPanel
                          current={printPaper}
                          onApply={(profile) => {
                            setPrintPaperSize(profile.paperSize);
                            setPrintPaperOrient(profile.orientation);
                            if (profile.customMm) {
                              setCustomPaperMm({
                                width: String(profile.customMm.width),
                                height: String(profile.customMm.height),
                              });
                            }
                            savePrintPrefs({ ...loadPrintPrefs(), printerProfileId: profile.id });
                            toastSuccess("Đã áp hồ sơ máy in", profile.name);
                          }}
                        />
                        <div className="space-y-1.5 rounded-lg border bg-muted/20 p-2">
                          <p className="text-[11px] font-semibold text-foreground">Khổ giấy in</p>
                          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
                            <Select
                              value={printPaperSize || AUTO_PAPER}
                              onValueChange={(v) => {
                                if (v === AUTO_PAPER) {
                                  setPrintPaperSize("");
                                  setPrintPaperOrient("");
                                  return;
                                }
                                setPrintPaperSize(v as PaperSize);
                                // Orientation is required for override to take effect.
                                setPrintPaperOrient((prev) => prev || "portrait");
                              }}
                            >
                              <SelectTrigger className="h-8 flex-1 text-xs" aria-label="Khổ giấy">
                                <SelectValue placeholder="Theo template" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={AUTO_PAPER}>Theo template (tự động)</SelectItem>
                                <SelectItem value="A4">A4</SelectItem>
                                <SelectItem value="A5">A5</SelectItem>
                                <SelectItem value="A6">A6</SelectItem>
                                <SelectItem value="Letter">Letter</SelectItem>
                                <SelectItem value="Legal">Legal</SelectItem>
                                <SelectItem value="Thermal80">Nhiệt 80mm</SelectItem>
                                <SelectItem value="Custom">Tùy chỉnh</SelectItem>
                              </SelectContent>
                            </Select>
                            <Select
                              value={printPaperOrient || AUTO_PAPER}
                              onValueChange={(v) => {
                                if (v === AUTO_PAPER) {
                                  setPrintPaperOrient("");
                                  return;
                                }
                                setPrintPaperOrient(v as Orientation);
                                // Size is required together with orientation.
                                setPrintPaperSize((prev) => prev || "A4");
                              }}
                            >
                              <SelectTrigger
                                className="h-8 w-full text-xs sm:w-[8.5rem]"
                                aria-label="Hướng giấy"
                              >
                                <SelectValue placeholder="Hướng" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={AUTO_PAPER}>Theo template</SelectItem>
                                <SelectItem value="portrait">Dọc (Portrait)</SelectItem>
                                <SelectItem value="landscape">Ngang (Landscape)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {printPaperSize === "Custom" && (
                            <div className="flex items-center gap-2 pt-0.5">
                              <label className="flex min-w-0 flex-1 items-center gap-1 text-[11px] text-muted-foreground">
                                <span className="shrink-0">Rộng</span>
                                <Input
                                  type="number"
                                  min={20}
                                  max={2000}
                                  step={1}
                                  className="h-8 text-xs"
                                  value={customPaperMm.width}
                                  onChange={(e) =>
                                    setCustomPaperMm((p) => ({ ...p, width: e.target.value }))
                                  }
                                  aria-label="Chiều rộng mm"
                                />
                                <span className="shrink-0">mm</span>
                              </label>
                              <span className="text-muted-foreground">×</span>
                              <label className="flex min-w-0 flex-1 items-center gap-1 text-[11px] text-muted-foreground">
                                <span className="shrink-0">Cao</span>
                                <Input
                                  type="number"
                                  min={20}
                                  max={2000}
                                  step={1}
                                  className="h-8 text-xs"
                                  value={customPaperMm.height}
                                  onChange={(e) =>
                                    setCustomPaperMm((p) => ({ ...p, height: e.target.value }))
                                  }
                                  aria-label="Chiều cao mm"
                                />
                                <span className="shrink-0">mm</span>
                              </label>
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground">
                            Đang dùng:{" "}
                            <span className="font-medium text-foreground">
                              {formatPaper(printPaper)}
                            </span>
                            {!printPaper && printPaperSize
                              ? " — chọn thêm Hướng (Dọc/Ngang) để áp dụng"
                              : null}
                          </p>
                        </div>
                        <div className="flex justify-end">
                          <Button size="sm" onClick={() => setProfileEditorOpen(false)}>
                            Xong
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <OnboardingTour
                    wizardStep={step}
                    replayNonce={tourReplayNonce}
                    forcePhase={tourForcePhase}
                    paused={presetPickerOpen || profileEditorOpen}
                  />
                </div>
              );
            }

export function stringifyPaper(size: PaperSize): string {
  return size;
}

/**
 * Value picker for a page condition: the user picks from the values the column
 * actually holds, never by typing, so a rule can't silently match nothing.
 */
function ConditionValuePicker({
  options,
  selected,
  onChange,
}: {
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [search, setSearch] = React.useState("");
  const needle = search.trim().toLowerCase();
  const filtered = options.filter((o) => o.toLowerCase().includes(needle)).slice(0, 300);

  function toggle(value: string) {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
    );
  }

  return (
    <div className="space-y-1.5">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((v) => (
            <Badge
              key={v}
              variant="secondary"
              className="cursor-pointer gap-1 text-[10px]"
              onClick={() => toggle(v)}
              title="Bỏ chọn"
            >
              {v} <X className="h-2.5 w-2.5" />
            </Badge>
          ))}
        </div>
      )}
      <Input
        className="h-8 text-xs"
        placeholder={`Tìm trong ${options.length} giá trị đang có…`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="max-h-32 overflow-auto rounded border">
        {filtered.length === 0 ? (
          <p className="p-2 text-[11px] text-muted-foreground">Không có giá trị nào khớp.</p>
        ) : (
          filtered.map((value) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 border-b px-2 py-1 text-xs last:border-b-0 hover:bg-muted"
            >
              <Checkbox checked={selected.includes(value)} onCheckedChange={() => toggle(value)} />
              <span className="truncate">{value}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}